import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import "../components/Advance/IncentivesAdvances.css";
import MonthPicker from "../components/MonthPicker/index.js";
import CustomPagination from "../../../components/CustomPagination";
import { commonGetData, commonSaveData, commonSaveDataOne, commonGetDataOne, commonGetDataTwo, commonGetDataThird, initCommonState } from "../../../redux/slices/commonSlice";
import { payrollProcessPath, runPayrollPath } from "../../../services/config";

const C = {
  bg: "var(--bg-page)",
  surface: "var(--bg-card)",
  elevated: "var(--pay-surface-2)",
  accent: "var(--primary)",
  accentHover: "var(--primary-hover)",
  muted: "var(--text-muted)",
  success: "var(--color-success)",
  warning: "var(--color-warning)",
  error: "var(--color-danger)",
  border: "var(--border)",
  borderAccent: "rgba(134,96,248,0.2)",
};

const s = {
  card: {
    background: "var(--bg-card)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
  },
  elevated: {
    background: "var(--pay-surface-2)",
    border: "1px solid var(--border)",
    borderRadius: 8,
  },
  btnPrimary: {
    background: "var(--primary)",
    color: "#fff",
    padding: "10px 22px",
    borderRadius: 24,
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
   fontSize: 12,
    fontFamily: "var(--font)",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    boxShadow: "0 4px 15px rgba(134,96,248,0.35)",
    transition: "all 0.2s",
  },
  btnSecondary: {
    background: "rgba(169,163,201,0.1)",
    color: "var(--text-primary)",
    padding: "9px 18px",
    borderRadius: 24,
    fontWeight: 500,
    border: "1px solid var(--border)",
    cursor: "pointer",
   fontSize: 12,
    fontFamily: "var(--font)",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    transition: "all 0.2s",
  },
  btnGhost: {
    background: "transparent",
    color: "var(--text-muted)",
    padding: "8px 14px",
    borderRadius: 20,
    fontWeight: 500,
    border: "1px solid var(--border)",
    cursor: "pointer",
    fontSize: 12,
    fontFamily: "var(--font)",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    transition: "all 0.2s",
  },
  btnSuccess: {
    background: "rgba(74,222,128,0.1)",
    color: "var(--color-success)",
    padding: "7px 14px",
    borderRadius: 18,
    fontWeight: 500,
    border: "1px solid rgba(74,222,128,0.25)",
    cursor: "pointer",
    fontSize: 12,
    fontFamily: "var(--font)",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  btnDanger: {
    background: "rgba(239,68,68,0.1)",
    color: "var(--color-danger)",
    padding: "7px 14px",
    borderRadius: 18,
    fontWeight: 500,
    border: "1px solid rgba(239,68,68,0.25)",
    cursor: "pointer",
    fontSize: 12,
    fontFamily: "var(--font)",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  input: {
    background: "var(--input-bg)",
    border: "1px solid var(--input-border)",
    borderRadius: 8,
    padding: "8px 12px",
    color: "var(--input-color)",
   fontSize: 12,
    fontFamily: "var(--font)",
    outline: "none",
    width: "20%",
    boxSizing: "border-box",
  },
  select: {
    background: "var(--input-bg)",
    border: "1px solid var(--input-border)",
    borderRadius: 8,
    padding: "8px 12px",
    color: "var(--input-color)",
   fontSize: 12,
    fontFamily: "var(--font)",
    outline: "none",
    cursor: "pointer",
  },
};

// ─── Status Chip ─────────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const map = {
    Ready:     { bg: "rgba(74,222,128,0.1)",  color: C.success, border: "rgba(74,222,128,0.25)"  },
    Pending:   { bg: "rgba(245,158,11,0.1)",  color: C.warning, border: "rgba(245,158,11,0.25)"  },
    Review:    { bg: "rgba(245,158,11,0.1)",  color: C.warning, border: "rgba(245,158,11,0.25)"  },
    Blocked:   { bg: "rgba(239,68,68,0.1)",   color: C.error,   border: "rgba(239,68,68,0.25)"   },
    Locked:    { bg: "rgba(134,96,248,0.1)",  color: C.accent,  border: "rgba(134,96,248,0.25)"  },
    Corrected: { bg: "rgba(59,130,246,0.1)",  color: "#3B82F6", border: "rgba(59,130,246,0.25)"  },
    Approved:  { bg: "rgba(59,130,246,0.1)",  color: "#3B82F6", border: "rgba(59,130,246,0.25)"  },
    Urgent:    { bg: "rgba(239,68,68,0.1)",   color: C.error,   border: "rgba(239,68,68,0.25)"   },
    High:      { bg: "rgba(239,68,68,0.08)",  color: C.error,   border: "rgba(239,68,68,0.2)"    },
    Medium:    { bg: "rgba(245,158,11,0.08)", color: C.warning, border: "rgba(245,158,11,0.2)"   },
    Low:       { bg: "rgba(74,222,128,0.08)", color: C.success, border: "rgba(74,222,128,0.2)"   },
  };
  const t = map[status] || map["Pending"];
  return (
    <span style={{
      background: t.bg, color: t.color,
      border: `1px solid ${t.border}`,
      padding: "4px 12px", borderRadius: 20,fontSize: 12, fontWeight: 600,
    }}>{status}</span>
  );
};

// ─── TAB ICONS ────────────────────────────────────────────────────────────────
const NAV_ICONS = {
  inputs: "fa-calendar-check",
  detail: "fa-magnifying-glass-chart",
  queue:  "fa-list-check",
};

// ─── Side Nav — hover expand / auto collapse ──────────────────────────────────
const Nav = ({ activeTab, setActiveTab }) => {
  const [expanded, setExpanded] = useState(false);

  const tabs = [
    { key: "inputs", label: "Attendance Inputs" },
    { key: "queue",  label: "Approval Queue"    },
  ];

  return (
    <aside
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      style={{
        width:    expanded ? 220 : 56,
        minWidth: expanded ? 220 : 56,
        height: "100%",
        background: "var(--pay-surface)",
        borderRight: `1px solid var(--pay-border)`,
        display: "flex",
        flexDirection: "column",
        transition: "width 0.25s cubic-bezier(0.4,0,0.2,1), min-width 0.25s cubic-bezier(0.4,0,0.2,1)",
        overflow: "hidden",
        flexShrink: 0,
        zIndex: 50,
      }}
    >
      {/* ── Header ── */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "0 13px",
        height: 56,
        borderBottom: `1px solid var(--pay-border)`,
        flexShrink: 0,
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}>
        {/* Logo mark */}
        <div style={{
          width: 30, height: 30, borderRadius: 8,
          background: "linear-gradient(135deg, #8660F8 0%, #5b3fd4 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
          boxShadow: "0 0 10px rgba(134,96,248,0.4)",
        }}>
          <i className="fa-solid fa-calendar-check" style={{ color: "#fff",fontSize: 12 }} />
        </div>

        {/* Title — fades in on hover */}
        <div style={{
          opacity: expanded ? 1 : 0,
          transform: expanded ? "translateX(0)" : "translateX(-8px)",
          transition: "opacity 0.2s ease, transform 0.2s ease",
          pointerEvents: "none",
        }}>
          <div style={{fontSize: 12, fontWeight: 700, color: "var(--pay-text)", lineHeight: 1.2 }}>
            Attendance
          </div>
          <div style={{ fontSize: 10, color: "var(--pay-text-muted)", lineHeight: 1.2 }}>
            Input & Review
          </div>
        </div>
      </div>

      {/* ── Nav Items ── */}
      <nav1 style={{ padding: "10px 8px", flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        {tabs.map(t => {
          const isActive = activeTab === t.key;
          const icon = NAV_ICONS[t.key];
          return (
            <div
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              title={!expanded ? t.label : ""}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "9px 10px", borderRadius: 10, cursor: "pointer",
                marginBottom: 3,
                background: isActive ? "rgba(134,96,248,0.15)" : "transparent",
                border: isActive ? "1px solid rgba(134,96,248,0.3)" : "1px solid transparent",
                transition: "all 0.15s ease",
                position: "relative", whiteSpace: "nowrap",
                userSelect: "none",
              }}
              onMouseEnter={e => {
                if (!isActive) {
                  e.currentTarget.style.background = "rgba(134,96,248,0.07)";
                  e.currentTarget.style.border = "1px solid rgba(134,96,248,0.15)";
                }
              }}
              onMouseLeave={e => {
                if (!isActive) {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.border = "1px solid transparent";
                }
              }}
            >
              {/* Active bar */}
              {isActive && (
                <div style={{
                  position: "absolute", left: 0, top: "18%", bottom: "18%",
                  width: 3, borderRadius: "0 3px 3px 0",
                  background: C.accent, boxShadow: "0 0 8px rgba(134,96,248,0.7)",
                }} />
              )}

              {/* Icon box */}
              <div style={{
                width: 30, height: 30, borderRadius: 8,
                background: isActive ? "rgba(134,96,248,0.22)" : "rgba(169,163,201,0.07)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, transition: "background 0.15s",
              }}>
                <i className={`fa-solid ${icon}`} style={{
                 fontSize: 12,
                  color: isActive ? C.accent : C.muted,
                  transition: "color 0.15s",
                }} />
              </div>

              {/* Label — slides in on expand */}
              <span style={{
               fontSize: 12,
                fontWeight: isActive ? 600 : 500,
                color: isActive ? "var(--pay-text)" : "var(--pay-text-muted)",
                opacity: expanded ? 1 : 0,
                transform: expanded ? "translateX(0)" : "translateX(-6px)",
                transition: "opacity 0.2s ease, transform 0.2s ease, color 0.15s",
                pointerEvents: "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}>
                {t.label}
              </span>
            </div>
          );
        })}
      </nav1>

      {/* ── Bottom badge ── */}
      <div style={{
        padding: "12px 8px",
        borderTop: `1px solid var(--pay-border)`,
        flexShrink: 0, overflow: "hidden",
      }}>
        <div style={{
          background: "rgba(134,96,248,0.1)",
          border: "1px solid rgba(134,96,248,0.22)",
          borderRadius: 8, padding: "8px 10px",
          display: "flex", alignItems: "center", gap: 8,
          whiteSpace: "nowrap",
        }}>
          <i className="fa-solid fa-calendar-days"
            style={{ color: C.accent,fontSize: 12, flexShrink: 0 }} />
          <span style={{
           fontSize: 12, fontWeight: 600, color: "var(--pay-text)",
            opacity: expanded ? 1 : 0,
            transform: expanded ? "translateX(0)" : "translateX(-6px)",
            transition: "opacity 0.2s ease, transform 0.2s ease",
            pointerEvents: "none",
          }}>
            {new Date().toLocaleString("default", { month: "short" })} {new Date().getFullYear()}
          </span>
        </div>
      </div>
    </aside>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PAGE 1 — ATTENDANCE INPUTS
// ════════════════════════════════════════════════════════════════════════════

const SOURCE_COLOR_MAP = {
  System:    "var(--primary)",
  Manager:   "#C084FC",
  Manual:    "var(--color-warning)",
  Corrected: "var(--color-warning)",
  Imported:  "#22D3EE",
};

const formatInr = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const formatPreviewMonthLabel = (ym) => {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return "Selected month";
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString("default", { month: "long", year: "numeric" });
};

/** Maps runPayroll preview API employee → Detail Review row shape */
const mapPreviewEmployeeToDetail = (e) => {
  const att = e.attendance || {};
  const comps = e.components || {};
  const name = e.staffName || "—";
  const lop =
    (att.absent ?? 0) + (att.unpaidLeave ?? 0) + (att.half ? Math.ceil(att.half / 2) : 0);
  return {
    staffId: e.staffId,
    name,
    empId: e.employeeId || String(e.staffId ?? ""),
    dept: e.department || "—",
    present: att.present ?? 0,
    lop,
    ot: att.overtimeHours ?? 0,
    payable: e.payableDays ?? 0,
    source: "Payroll Preview",
    sourceColor: C.accent,
    status: e.success === false ? "Review" : "Ready",
    updated: "Preview",
    avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=8660F8&color=fff`,
    grossSalary: e.grossSalary,
    netSalary: e.netSalary,
    tds: e.tds,
    components: comps,
    attendance: att,
    fromApi: true,
  };
};

const unwrapPayrollResponse = (data) => {
  if (!data) return {};
  // data.data is a direct array → wrap it so callers can use payload.list
  if (data.data && Array.isArray(data.data)) return { list: data.data, summary: data.summary || null };
  // data.data is a nested object
  if (data.data && typeof data.data === "object") return data.data;
  return data;
};

const mapInputEmployeeRow = (e) => {
  const name = e.staffName || e.name || "—";
  const src = e.source || e.attendanceSource || "System";
  return {
    name,
    empId: e.employeeId || e.empId || String(e.staffId ?? ""),
    staffId: e.staffId,
    dept: e.department || e.dept || "—",
    present: e.present ?? e.presentDays ?? e.attendance?.present ?? 0,
    lop: e.lop ?? e.lopDays ?? 0,
    ot: e.ot ?? e.otHours ?? e.attendance?.overtimeHours ?? 0,
    payable: e.payable ?? e.payableDays ?? 0,
    source: src,
    sourceColor: SOURCE_COLOR_MAP[src] || C.accent,
    status: e.status || e.approvalStatus || "Pending",
    updated: e.updated || e.lastUpdated || "—",
    avatar:
      e.avatar ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=8660F8&color=fff`,
  };
};

const mapInputException = (e) => {
  const name = e.staffName || e.name || "—";
  const src = e.source || e.attendanceSource || "System";
  const type = (e.type || e.exceptionType || e.category || e.exceptionCategory || "").toString().toLowerCase();
  return {
    name,
    empId: e.employeeId || e.empId || String(e.staffId ?? ""),
    staffId: e.staffId,
    dept: e.department || e.dept || "—",
    type,
    category: type,
    lop: e.lop ?? e.lopDays ?? 0,
    ot: e.ot ?? e.otHours ?? 0,
    status: e.status || e.approvalStatus || "Pending",
    source: src,
    avatar:
      e.avatar ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=8660F8&color=fff`,
  };
};

const parseNumber = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string") {
    const numeric = Number(value.replace(/[^0-9.-]+/g, ""));
    return Number.isFinite(numeric) ? numeric : fallback;
  }
  return Number.isFinite(Number(value)) ? Number(value) : fallback;
};

const parsePercentage = (tag) => {
  if (typeof tag !== "string") return parseNumber(tag, 0);
  const cleaned = tag.replace("%", "").trim();
  return parseNumber(cleaned, 0);
};

const normalizeInputKpis = (kpis = {}) => {
  const totalDays = kpis.totalDays?.value ?? kpis.totalDays;
  const present = kpis.present?.value ?? kpis.present;
  const lop = kpis.lop?.value ?? kpis.lop;
  const ot = kpis.ot?.value ?? kpis.ot;
  const payable = kpis.payable?.value ?? kpis.payable;
  const pending = kpis.pending?.value ?? kpis.pending;

  return {
    totalDaysInMonth: parseNumber(totalDays, 31),
    present: {
      count: parseNumber(present, 0),
      percentage: parsePercentage(kpis.present?.tag ?? kpis.presentPercentage ?? kpis.presentPct),
      change: parseNumber(kpis.present?.change ?? 0, 0),
    },
    lopDays: {
      total: parseNumber(lop, 0),
      change: parseNumber(kpis.lop?.change ?? 0, 0),
    },
    otHours: {
      total: parseNumber(ot, 0),
      change: parseNumber(kpis.ot?.change ?? 0, 0),
    },
    payableDays: {
      total: parseNumber(payable, 0),
      status: kpis.payable?.tag ?? kpis.payableStatus ?? kpis.payableLabel ?? "Pending",
    },
    pendingCount: parseNumber(pending, 0),
    needsImmediateAction: kpis.needsImmediateAction ?? kpis.exceptionCount ?? 0,
    approvedThisMonth: parseNumber(kpis.approvedCount ?? kpis.approved ?? 0, 0),
    departmentChart: kpis.departmentChart ?? {},
    trendChart: kpis.trends ?? {},
  };
};

const mapEmployeeToDetail = (e) => ({
  staffId: e.staffId,
  name: e.name,
  empId: e.empId,
  dept: e.dept,
  present: e.present,
  lop: e.lop,
  ot: e.ot,
  payable: e.payable,
  source: e.source,
  sourceColor: e.sourceColor,
  status: e.status,
  updated: e.updated,
  avatar: e.avatar,
  grossSalary: e.grossSalary,
  netSalary: e.netSalary,
  tds: e.tds,
  components: e.components,
  attendance: e.attendance,
  fromApi: true,
});

const normalizeDayStatus = (status) => {
  const s = String(status || "present").toLowerCase();
  if (["present", "p", "wd"].includes(s)) return "present";
  if (["absent", "a"].includes(s)) return "absent";
  if (["lop", "lossofpay"].includes(s)) return "lop";
  if (["missing", "miss"].includes(s)) return "missing";
  if (["late"].includes(s)) return "late";
  if (["half", "halfday", "hd"].includes(s)) return "half";
  if (["leave", "paidleave", "pl"].includes(s)) return "leave";
  if (["holiday", "h"].includes(s)) return "holiday";
  if (["weekoff", "wo", "off"].includes(s)) return "weekoff";
  return s || "present";
};

const buildCalendarFromDayWise = (dayWise, month) => {
  if (!Array.isArray(dayWise) || !dayWise.length) return { days: [], details: {} };
  const details = {};
  const days = dayWise.map((d) => {
    const dateStr = d.date || d.attendanceDate || "";
    const dayNum = dateStr.includes("-")
      ? dateStr.split("-")[2]
      : String(d.day ?? d.dayNum ?? "").padStart(2, "0");
    const type = normalizeDayStatus(d.status || d.type || d.dayStatus);
    details[dayNum] = {
      in: d.in || d.clockIn || d.punchIn || "—",
      out: d.out || d.clockOut || d.punchOut || "—",
      worked: d.worked || d.workedHours || d.hours || "—",
      note: d.note || d.remarks || d.reason || null,
      badge: d.badge || d.statusLabel || null,
    };
    return { d: dayNum, type };
  });
  return { days, details, month };
};

const mapDetailResponse = (data, baseEmp) => {
  const payload = unwrapPayrollResponse(data);
  const att = payload.attendance || payload;
  const e = payload.employee || payload.staff || payload;
  const name = e.staffName || e.name || baseEmp?.name || "—";
  const adj = payload.adjustments || {};
  const lop =
    adj.lopDays ??
    att.lopDays ??
    att.lop ??
    (((att.absent ?? 0) + (att.unpaidLeave ?? 0) + (att.half ? Math.ceil(att.half / 2) : 0)) || baseEmp?.lop);
  const ot = adj.otHours ?? att.otHours ?? att.overtimeHours ?? att.ot ?? baseEmp?.ot ?? 0;
  const payable =
    adj.payableOverride ??
    adj.payableDays ??
    att.payableDays ??
    payload.payableDays ??
    baseEmp?.payable ??
    0;
  const requests = payload.requests || payload.regularizationRequests || [];
  const issues = payload.issues || [];
  const issueSummary = payload.issueSummary || {
    missingPunches: issues.filter((i) => /missing/i.test(i.type || i.issue || "")).length || att.missingPunches || 0,
    lateMarks: issues.filter((i) => /late/i.test(i.type || i.issue || "")).length || att.lateMarks || 0,
    halfDays: att.half ?? 0,
    pendingRequests: requests.filter((r) => (r.status || "").toLowerCase() === "pending").length,
  };
  return {
    ...baseEmp,
    staffId: e.staffId ?? baseEmp?.staffId,
    name,
    empId: e.employeeId || e.empId || baseEmp?.empId,
    dept: e.department || e.dept || baseEmp?.dept,
    branch: e.branch || e.branchName || e.location || baseEmp?.branch || "—",
    shift: e.shift || e.shiftName || baseEmp?.shift || "—",
    present: att.present ?? att.presentDays ?? baseEmp?.present ?? 0,
    lop,
    ot,
    payable,
    grossSalary: payload.grossSalary ?? e.grossSalary ?? baseEmp?.grossSalary,
    netSalary: payload.netSalary ?? e.netSalary ?? baseEmp?.netSalary,
    tds: payload.tds ?? e.tds ?? baseEmp?.tds,
    components: payload.components || baseEmp?.components,
    attendance: att,
    dayWise: payload.days || payload.dayWise || payload.calendar || [],
    requests,
    issues,
    issueSummary,
    auditTrail: payload.auditTrail || payload.audit || [],
    adjustments: adj,
    fromApi: true,
  };
};

const buildLockPayload = (action, userId, month, branchId, departmentId, shiftId) => {
  const body = { action, userId, month };
  if (branchId) body.branchId = branchId;
  if (departmentId) body.departmentId = departmentId;
  if (shiftId) body.shiftId = shiftId;
  return body;
};

const mapQueueItem = (item) => {
  const name = item.staffName || item.name || "—";
  const pri = item.priority || item.issuePriority || "Medium";
  const src = item.source || "System";
  return {
    id: item.id ?? item.itemId ?? item.queueId,
    staffId: item.staffId,
    name,
    empId: item.employeeId || item.empId || String(item.staffId ?? ""),
    dept: item.department || item.dept || "—",
    issue: item.issue || item.issueType || item.issueTitle || "—",
    detail: item.detail || item.issueDetail || item.description || "",
    date: item.date || item.issueDate || "—",
    day: item.day || item.dayName || "",
    source: src,
    srcColor: SOURCE_COLOR_MAP[src] || C.muted,
    priority: pri,
    status: item.status || "Pending",
    by: item.requestedBy || item.by || "—",
    ago: item.ago || item.requestedAgo || "",
    avatar:
      item.avatar ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=8660F8&color=fff`,
  };
};

const KpiCard = ({ icon, iconColor, label, value, tag, tagColor, glow }) => (
  <div style={{ ...s.card, padding: 10, position: "relative", overflow: "hidden", boxShadow: glow ? `0 0 12px ${glow}` : s.card.boxShadow }}>
    <div style={{ position: "absolute", right: -15, top: -15, width: 60, height: 60, background: `${iconColor}18`, borderRadius: "50%", filter: "blur(15px)" }} />
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4, color: C.muted, fontSize: 9, fontWeight: 500 }}>
        <i className={icon} style={{ color: iconColor, fontSize: 10 }} />{label}
      </div>
      <span style={{ fontSize: 8, fontWeight: 600, color: tagColor, background: `${tagColor}20`, padding: "2px 5px", borderRadius: 4 }}>{tag}</span>
    </div>
    <div style={{fontSize: 12, fontWeight: 700 }}>{value}</div>
  </div>
);

const ApprovalKpiCard = ({ icon, iconColor, borderColor, value, label, glow }) => {
  const [count, setCount] = useState(0);
  const [hovered, setHovered] = useState(false);
  const target = parseInt(value) || 0;
  useEffect(() => {
    let start = 0;
    const step = Math.ceil(target / (1200 / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); } else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target]);
  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{
      ...s.card, padding: 16, borderLeft: `3px solid ${borderColor}`,
      boxShadow: hovered ? `0 0 28px ${glow || borderColor + "55"}, 0 8px 32px rgba(0,0,0,0.3)` : glow ? `0 0 12px ${glow}` : s.card.boxShadow,
      transform: hovered ? "translateY(-4px) scale(1.02)" : "translateY(0) scale(1)",
      transition: "all 0.3s cubic-bezier(0.34,1.56,0.64,1)", cursor: "default", position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", inset: 0, background: `linear-gradient(135deg, ${borderColor}08 0%, transparent 60%)`, opacity: hovered ? 1 : 0, transition: "opacity 0.3s" }} />
      <div style={{ position: "relative", width: 36, height: 36, marginBottom: 10 }}>
        <div style={{ position: "absolute", inset: -4, borderRadius: "50%", border: `1px solid ${borderColor}`, opacity: hovered ? 0.6 : 0, transform: hovered ? "scale(1.3)" : "scale(1)", transition: "all 0.4s ease" }} />
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: hovered ? `${iconColor}30` : `${iconColor}18`, display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.3s" }}>
          <i className={`fa-solid ${icon}`} style={{ color: iconColor, fontSize: 12, transform: hovered ? "scale(1.15) rotate(-5deg)" : "scale(1) rotate(0deg)", transition: "transform 0.3s cubic-bezier(0.34,1.56,0.64,1)" }} />
        </div>
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: iconColor, marginBottom: 2, letterSpacing: hovered ? "1px" : "0px", transition: "letter-spacing 0.3s" }}>{count}</div>
      <div style={{ fontSize: 12, color: C.muted }}>{label}</div>
      <div style={{ position: "absolute", bottom: 0, left: 0, height: 2, background: `linear-gradient(to right, ${borderColor}, transparent)`, width: hovered ? "100%" : "30%", transition: "width 0.4s ease" }} />
    </div>
  );
};

const PlotlyBarChart = ({ departments, avgPayableDays, onBarClick }) => {
  const ref = useRef(null);
  useEffect(() => {
    const depts = departments && departments.length ? departments : ["Unknown"];
    const vals  = avgPayableDays && avgPayableDays.length ? avgPayableDays : [0];
    const load = () => {
      if (!window.Plotly || !ref.current) return;
      window.Plotly.newPlot(ref.current, [{ type: "bar", x: depts, y: vals, marker: { color: "rgba(134,96,248,0.85)", line: { color: "rgba(134,96,248,1)", width: 1 } }, hovertemplate: "<b>%{x}</b><br>Avg Days: %{y}<extra></extra>" }], { paper_bgcolor: "transparent", plot_bgcolor: "transparent", font: { color: "#9B97B2", size: 10, family: "inherit" }, margin: { t: 10, r: 10, b: 40, l: 30 }, xaxis: { gridcolor: "rgba(169,163,201,0.08)", tickfont: { size: 9 }, fixedrange: true }, yaxis: { gridcolor: "rgba(169,163,201,0.08)", tickfont: { size: 9 }, fixedrange: true }, hoverlabel: { bgcolor: getComputedStyle(document.documentElement).getPropertyValue('--pay-surface').trim() || "#ffffff", bordercolor: "rgba(134,96,248,0.4)", font: { color: getComputedStyle(document.documentElement).getPropertyValue('--pay-text').trim() || "#111827", size: 11 } } }, { displayModeBar: false, responsive: true });
      if (onBarClick) {
        ref.current.on('plotly_click', (data) => {
          const dept = data.points[0]?.x;
          if (dept) onBarClick(dept);
        });
      }
    };
    if (window.Plotly) load();
    else { const sc = document.createElement("script"); sc.src = "https://cdn.plot.ly/plotly-2.26.0.min.js"; sc.onload = load; document.head.appendChild(sc); }
    return () => { if (ref.current && window.Plotly) window.Plotly.purge(ref.current); };
  }, [departments, avgPayableDays, onBarClick]);
  return <div ref={ref} style={{ height: 220, cursor: 'pointer' }} />;
};

const PlotlyLineChart = ({ months, lopDays, otHours, onPointClick }) => {
  const ref = useRef(null);
  useEffect(() => {
    const xVals = months && months.length ? months : ["Jan","Feb","Mar","Apr"];
    const lopY  = lopDays && lopDays.length ? lopDays : [0,0,0,0];
    const otY   = otHours && otHours.length ? otHours : [0,0,0,0];
    const load = () => {
      if (!window.Plotly || !ref.current) return;
      window.Plotly.newPlot(ref.current, [
        { type: "scatter", mode: "lines+markers", name: "LOP Days", x: xVals, y: lopY, line: { color: "#EF4444", width: 2 }, marker: { color: "#EF4444", size: 6 }, hovertemplate: "%{x}: <b>%{y}</b><extra>LOP Days</extra>" },
        { type: "scatter", mode: "lines+markers", name: "OT Hours", x: xVals, y: otY, line: { color: "#F59E0B", width: 2 }, marker: { color: "#F59E0B", size: 6 }, hovertemplate: "%{x}: <b>%{y}</b><extra>OT Hours</extra>" },
      ], { paper_bgcolor: "transparent", plot_bgcolor: "transparent", font: { color: "#9B97B2", size: 10, family: "inherit" }, margin: { t: 10, r: 10, b: 40, l: 36 }, xaxis: { gridcolor: "rgba(169,163,201,0.08)", tickfont: { size: 9 }, fixedrange: true }, yaxis: { gridcolor: "rgba(169,163,201,0.08)", tickfont: { size: 9 }, fixedrange: true }, legend: { orientation: "h", x: 0, y: 1.15, font: { size: 10, color: "#9B97B2" }, bgcolor: "transparent" }, hoverlabel: { bgcolor: getComputedStyle(document.documentElement).getPropertyValue('--pay-surface').trim() || "#ffffff", bordercolor: "rgba(134,96,248,0.4)", font: { color: getComputedStyle(document.documentElement).getPropertyValue('--pay-text').trim() || "#111827", size: 11 } } }, { displayModeBar: false, responsive: true });
      if (onPointClick) {
        ref.current.on('plotly_click', (data) => {
          const month = data.points[0]?.x;
          if (month) onPointClick(month);
        });
      }
    };
    if (window.Plotly) load();
    else { const sc = document.createElement("script"); sc.src = "https://cdn.plot.ly/plotly-2.26.0.min.js"; sc.onload = load; document.head.appendChild(sc); }
    return () => { if (ref.current && window.Plotly) window.Plotly.purge(ref.current); };
  }, [months, lopDays, otHours, onPointClick]);
  return <div ref={ref} style={{ height: 220, cursor: 'pointer' }} />;
};

const DrawerPanel = ({ open, onClose, employee }) => {
  const [lopAdj, setLopAdj] = useState("0");
  const [otAdj,  setOtAdj]  = useState("0");
  const [remarks, setRemarks] = useState("");

  // Reset fields when a new employee is selected
  useEffect(() => {
    setLopAdj("0");
    setOtAdj("0");
    setRemarks("");
  }, [employee?.staffId]);

  if (!open) return null;

  const emp = employee || {};
  const name    = emp.name    || "—";
  const empId   = emp.empId   || "—";
  const dept    = emp.dept    || "—";
  const status  = emp.status  || "Pending";
  const present = emp.present ?? 0;
  const lop     = emp.lop     ?? 0;
  const ot      = emp.ot      ?? 0;
  const payable = emp.payable ?? 0;
  const avatar  = emp.avatar  || `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=8660F8&color=fff`;

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", zIndex: 100 }} />
      <div style={{ position: "fixed", right: 0, top: 0, bottom: 0, width: 400, background: C.surface, borderLeft: `1px solid ${C.border}`, zIndex: 101, overflowY: "auto", transform: "translateX(0)", transition: "transform 0.3s ease" }}>
        <div style={{ position: "sticky", top: 0, background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>Employee Attendance Details</span>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: C.muted, display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%" }}><i className="fa-solid fa-xmark" /></button>
        </div>
        <div style={{ padding: 20 }}>
          {/* ── Profile ── */}
          <div style={{ display: "flex", gap: 14, paddingBottom: 18, borderBottom: `1px solid ${C.border}`, marginBottom: 18 }}>
            <img src={avatar} style={{ width: 64, height: 64, borderRadius: 10, objectFit: "cover", border: `2px solid rgba(134,96,248,0.3)`, flexShrink: 0 }} alt={name} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
              <div style={{ color: C.muted, fontSize: 11, marginBottom: 8 }}>{dept} · {empId}</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <StatusChip status={status} />
                <span style={{ fontSize: 11, padding: "3px 10px", background: "rgba(134,96,248,0.1)", color: C.accent, border: `1px solid rgba(134,96,248,0.25)`, borderRadius: 20 }}>{dept}</span>
              </div>
            </div>
          </div>

          {/* ── Stats grid ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 18 }}>
            {[
              ["Present Days", present, C.success],
              ["LOP Days",     lop,     C.error],
              ["OT Hours",     ot,      C.warning],
              ["Payable Days", payable, "var(--text-primary)"],
            ].map(([label, value, color]) => (
              <div key={label} style={{ background: "var(--pay-surface-2)", border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
              </div>
            ))}
          </div>

          {/* ── Manual Adjustments ── */}
          <div style={{ background: "var(--pay-surface-2)", border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, marginBottom: 14 }}>
            <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
              <i className="fa-solid fa-pen-to-square" style={{ color: C.accent }} /> Manual Adjustments
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 5 }}>Additional LOP Days</label>
              <input type="number" value={lopAdj} onChange={e => setLopAdj(e.target.value)} style={{ ...s.input, width: "100%" }} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 5 }}>Additional OT Hours</label>
              <input type="number" value={otAdj} onChange={e => setOtAdj(e.target.value)} style={{ ...s.input, width: "100%" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 5 }}>Remarks</label>
              <textarea rows={3} placeholder="Add notes..." value={remarks} onChange={e => setRemarks(e.target.value)} style={{ ...s.input, resize: "none", width: "100%" }} />
            </div>
          </div>

          {/* ── Actions ── */}
          <div style={{ display: "flex", gap: 10 }}>
            <button style={{ ...s.btnSecondary, flex: 1, justifyContent: "center", fontSize: 11 }}>
              <i className="fa-solid fa-clock-rotate-left" /> View History
            </button>
            <button style={{ ...s.btnPrimary, flex: 1, justifyContent: "center", fontSize: 11 }}>
              <i className="fa-solid fa-check" /> Save Changes
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const AttendanceInputs = ({ setActiveTab, onPayrollPreviewReady }) => {
  const dispatch       = useDispatch();
  const commonStore    = useSelector(s => s.common);
  const branchList     = useSelector(s => s.branch?.brnGetData     || []);
  const departmentList = useSelector(s => s.department?.depGetData || []);
  const shiftList      = useSelector(s => s.shift?.shiftGetData   || []);
  const user           = JSON.parse(localStorage.getItem("user_data")) || {};

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const todayD = new Date();
  const [selectedMonth, setSelectedMonth] = useState(
    `${todayD.getFullYear()}-${String(todayD.getMonth() + 1).padStart(2, '0')}`
  );
  const [selectedBranch, setSelectedBranch]     = useState('');
  const [selectedDept,   setSelectedDept]       = useState('');
  const [selectedShift,  setSelectedShift]      = useState('');
  const [selectedType,   setSelectedType]       = useState('');
  const [searchQuery,    setSearchQuery]         = useState('');

  // ── Aggregates API state ──
  const [aggregates, setAggregates] = useState(null);
  const [inputEmployees, setInputEmployees] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [departmentChartData, setDepartmentChartData] = useState({ departments: [], avgPayableDays: [] });
  const [trendChartData, setTrendChartData] = useState({ months: [], lopDays: [], otHours: [] });
  const [inputsLoading, setInputsLoading] = useState(false);
  const payrollActionRef = useRef(null);

  // ── Fetch inputs from payrollProcess (action: fetchInputs) ──
  const fetchAggregates = useCallback((month) => {
    payrollActionRef.current = "fetchInputs";
    setInputsLoading(true);
    dispatch(commonSaveData({
      path: payrollProcessPath,
      data: {
        action:       "fetchInputs",
        userId:       user.user_id,
        month:        month || selectedMonth,
        branchId:     selectedBranch || "",
        departmentId: selectedDept || "",
        shiftId:      selectedShift || "",
        searchQuery:  searchQuery || "",
      },
    }));
  }, [dispatch, user.user_id, selectedMonth, selectedBranch, selectedDept, selectedShift, searchQuery]);

  useEffect(() => {
    fetchAggregates(selectedMonth);
  }, [selectedMonth, selectedBranch, selectedDept, selectedShift]); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(() => fetchAggregates(selectedMonth), 400);
    return () => clearTimeout(t);
  }, [searchQuery]); // eslint-disable-line

  // ── Handle fetchInputs / runPayroll API response ──
  useEffect(() => {
    if (!payrollActionRef.current) return;

    // ── run payroll preview response ──
    if (payrollActionRef.current === "runPayroll") {
      if (commonStore.commonSuccess && commonStore.commonData) {
        const raw = commonStore.commonData;
        // API shape: { data: { selectedMonth, employees: [...] } }
        const body = raw?.data ?? raw ?? {};
        const apiMonth = body.selectedMonth || selectedMonth;
        const apiEmployees = Array.isArray(body.employees) ? body.employees : [];

        const mappedEmployees = apiEmployees.map((e) => ({
          staffId:     e.staffId,
          name:        e.staffName || "—",
          empId:       e.employeeId || String(e.staffId || ""),
          dept:        e.department || e.dept || "—",
          present:     e.attendance?.present ?? 0,
          lop:         e.attendance?.unpaidLeave ?? e.attendance?.absent ?? 0,
          ot:          e.attendance?.overtimeHours ?? 0,
          payable:     e.payableDays ?? 0,
          grossSalary: e.grossSalary ?? 0,
          netSalary:   e.netSalary ?? 0,
          tds:         e.tds ?? 0,
          components:  e.components ?? {},
          attendance:  e.attendance ?? {},
          status:      e.success ? "Ready" : "Pending",
          source:      "API",
          avatar:      `https://ui-avatars.com/api/?name=${encodeURIComponent(e.staffName || "E")}&background=8660F8&color=fff`,
          fromApi:     true,
        }));

        onPayrollPreviewReady?.({ selectedMonth: apiMonth, employees: mappedEmployees });
        payrollActionRef.current = null;
        setRunPayrollLoading(false);
        dispatch(initCommonState());
        showToast(`Payroll preview ready — ${mappedEmployees.length} employee${mappedEmployees.length === 1 ? "" : "s"} loaded`);
        setTimeout(() => setActiveTab("detail"), 300);
      }
      if (commonStore.commonError) {
        showToast(commonStore.commonMessage || "Run payroll failed", "error");
        payrollActionRef.current = null;
        setRunPayrollLoading(false);
        dispatch(initCommonState());
      }
      return;
    }

    if (payrollActionRef.current !== "fetchInputs") return;
      if (commonStore.commonSuccess && commonStore.commonData) {
      const payload = unwrapPayrollResponse(commonStore.commonData);
      const rows = payload.employees || payload.staffList || payload.list || [];
      if (Array.isArray(rows)) setInputEmployees(rows.map(mapInputEmployeeRow));

      const kpiSource = payload.kpis || payload;
      setAggregates(normalizeInputKpis(kpiSource));

      const exceptionsPayload = payload.exceptions || {};
      const exceptionList = [];
      if (Array.isArray(exceptionsPayload.missingPunch)) {
        exceptionList.push(...exceptionsPayload.missingPunch.map((e) => ({ ...e, category: 'missing', type: 'missingPunch' })));
      }
      if (Array.isArray(exceptionsPayload.excessOT)) {
        exceptionList.push(...exceptionsPayload.excessOT.map((e) => ({ ...e, category: 'ot', type: 'excessOT' })));
      }
      if (Array.isArray(exceptionsPayload.unapprovedAbsences)) {
        exceptionList.push(...exceptionsPayload.unapprovedAbsences.map((e) => ({ ...e, category: 'absences', type: 'unapprovedAbsences' })));
      }
      setExceptions(exceptionList);

      if (Array.isArray(payload.departmentChart)) {
        setDepartmentChartData({
          departments: payload.departmentChart.map((item) => item.department ?? item.dept ?? "Unknown"),
          avgPayableDays: payload.departmentChart.map((item) => parseNumber(item.avgPayable ?? item.avgPayableDays ?? item.value ?? 0)),
        });
      } else {
        setDepartmentChartData(payload.departmentChart || payload.kpis?.departmentChart || departmentChartData);
      }

      if (Array.isArray(payload.trends)) {
        setTrendChartData({
          months: payload.trends.map((item) => item.month || item.label || ""),
          lopDays: payload.trends.map((item) => parseNumber(item.lop ?? item.lopDays ?? 0)),
          otHours: payload.trends.map((item) => parseNumber(item.ot ?? item.otHours ?? 0)),
        });
      } else {
        setTrendChartData(payload.trends || payload.kpis?.trends || trendChartData);
      }

      payrollActionRef.current = null;
      setInputsLoading(false);
      dispatch(initCommonState());
    }
    if (commonStore.commonError) {
      payrollActionRef.current = null;
      setInputsLoading(false);
      dispatch(initCommonState());
    }
  }, [commonStore.commonSuccess, commonStore.commonError, commonStore.commonData, dispatch]);

  // ── Derived values from API (with safe fallbacks) ──
  const totalDays        = aggregates?.totalDaysInMonth ?? 31;
  const presentCount     = aggregates?.present?.count ?? 0;
  const presentPct       = aggregates?.present?.percentage ?? 0;
  const presentChange    = aggregates?.present?.change ?? 0;
  const lopTotal         = aggregates?.lopDays?.total ?? 0;
  const lopChange        = aggregates?.lopDays?.change ?? 0;
  const otTotal          = aggregates?.otHours?.total ?? 0;
  const otChange         = aggregates?.otHours?.change ?? 0;
  const payableDaysTotal = aggregates?.payableDays?.total ?? 0;
  const payableStatus    = aggregates?.payableDays?.status ?? "Pending";
  const pendingCountAPI  = aggregates?.pendingCount ?? 0;
  const needsActionCount = aggregates?.needsImmediateAction ?? exceptions.length;
  const approvedCount_   = aggregates?.approvedThisMonth ?? 0;
  const deptChartDepts   = departmentChartData?.departments || departmentChartData?.labels || [];
  const deptChartVals    = departmentChartData?.avgPayableDays || departmentChartData?.data || departmentChartData?.values || [];
  const trendMonths      = trendChartData?.months || trendChartData?.labels || [];
  const trendLop         = trendChartData?.lopDays || trendChartData?.lop || trendChartData?.data?.lopDays || [];
  const trendOt          = trendChartData?.otHours || trendChartData?.ot || trendChartData?.data?.otHours || [];

  // ── New feature state ──
  const [isLocked,          setIsLocked]          = useState(false);
  const [lockLoading,       setLockLoading]       = useState(false);
  const [showLockConfirm,   setShowLockConfirm]   = useState(false);
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);
  const lockActionRef       = useRef(null);
  const [pendingCount,      setPendingCount]       = useState(pendingCountAPI);
  const [approvedCount,     setApprovedCount]      = useState(approvedCount_);
  const [exceptionModal,    setExceptionModal]     = useState(null); // null | 'missing' | 'ot' | 'absences'
  const [deptModal,         setDeptModal]          = useState(null); // null | dept name
  const [trendModal,        setTrendModal]         = useState(null); // null | month name

  // Sync pending/approved counts when API data arrives
  useEffect(() => {
    setPendingCount(pendingCountAPI);
  }, [pendingCountAPI]);

  useEffect(() => {
    setApprovedCount(approvedCount_);
  }, [approvedCount_]);

  const [toast, setToast] = useState(null); // { msg, type }
  const fileInputRef = useRef(null);

  // ── Toast helper ──
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const [runPayrollLoading, setRunPayrollLoading] = useState(false);

  // ── Auto Resolve ──
  const handleAutoResolve = () => {
    const resolved = pendingCount;
    setPendingCount(0);
    setApprovedCount(a => a + resolved);
    showToast(`Auto Resolved ${resolved} pending cases successfully`);
  };

  const fetchLockStatus = useCallback(() => {
    lockActionRef.current = "getLockStatus";
    dispatch(commonSaveData({
      path: payrollProcessPath,
      data: buildLockPayload("getLockStatus", user.user_id, selectedMonth, selectedBranch, selectedDept, selectedShift),
    }));
  }, [dispatch, user.user_id, selectedMonth, selectedBranch, selectedDept, selectedShift]);

  useEffect(() => {
    fetchLockStatus();
  }, [selectedMonth, selectedBranch, selectedDept, selectedShift]); // eslint-disable-line

  useEffect(() => {
    if (!lockActionRef.current) return;
    if (commonStore.commonSuccess && commonStore.commonData) {
      const payload = unwrapPayrollResponse(commonStore.commonData);
      if (lockActionRef.current === "getLockStatus" && payload.locked !== undefined) {
        setIsLocked(Boolean(payload.locked));
      } else if (lockActionRef.current === "lockAttendance" || lockActionRef.current === "unlockAttendance") {
        setIsLocked(lockActionRef.current === "lockAttendance");
        showToast(commonStore.commonMessage || (lockActionRef.current === "lockAttendance" ? `Attendance locked for ${selectedMonth}` : `Attendance unlocked for ${selectedMonth}`));
        if (lockActionRef.current === "lockAttendance") setShowLockConfirm(false);
        else setShowUnlockConfirm(false);
      }
      lockActionRef.current = null;
      setLockLoading(false);
      dispatch(initCommonState());
    }
    if (commonStore.commonError && lockActionRef.current) {
      showToast(commonStore.commonMessage || "Lock action failed", "error");
      lockActionRef.current = null;
      setLockLoading(false);
      dispatch(initCommonState());
    }
  }, [commonStore.commonSuccess, commonStore.commonError, commonStore.commonData, commonStore.commonMessage, dispatch, selectedMonth]);

  const handleLockConfirm = () => {
    setShowLockConfirm(false);
    setIsLocked(true);
    setLockLoading(true);
    lockActionRef.current = "lockAttendance";
    showToast(`Attendance locked for ${selectedMonth}`);
    dispatch(commonSaveData({
      path: payrollProcessPath,
      data: buildLockPayload("lockAttendance", user.user_id, selectedMonth, selectedBranch, selectedDept, selectedShift),
    }));
  };

  const handleUnlockConfirm = () => {
    setShowUnlockConfirm(false);
    setIsLocked(false);
    setLockLoading(true);
    lockActionRef.current = "unlockAttendance";
    showToast(`Attendance unlocked for ${selectedMonth}`, "warning");
    dispatch(commonSaveData({
      path: payrollProcessPath,
      data: buildLockPayload("unlockAttendance", user.user_id, selectedMonth, selectedBranch, selectedDept, selectedShift),
    }));
  };

  const sourceEmployees = inputEmployees;
  const procEmployees = sourceEmployees.filter(emp => {
    if (selectedType && emp.source?.toLowerCase() !== selectedType.toLowerCase()) return false;
    return true;
  });

  // ── Exception employees by type ──
  const getExceptionCategory = (exception) => {
    const type = (exception.type || exception.exceptionType || exception.category || exception.exceptionCategory || exception.status || "").toString().toLowerCase();
    if (/missing|punch/.test(type)) return 'missing';
    if (/ot|overtime/.test(type)) return 'ot';
    if (/absent|absence|unapproved/.test(type)) return 'absences';
    return 'other';
  };

  const getExceptionEmployees = (type) => {
    if (exceptions.length) {
      return exceptions.filter((e) => getExceptionCategory(e) === type).slice(0, type === 'missing' ? 8 : type === 'ot' ? 6 : 4);
    }
    const all = sourceEmployees;
    if (type === 'missing')   return all.filter(e => e.lop > 2).slice(0, 8);
    if (type === 'ot')        return all.filter(e => e.ot > 10).slice(0, 6);
    if (type === 'absences')  return all.filter(e => e.status === 'Review' || e.status === 'Blocked').slice(0, 4);
    return [];
  };

  const exceptionCounts = {
    missing:  getExceptionEmployees('missing').length,
    ot:       getExceptionEmployees('ot').length,
    absences: getExceptionEmployees('absences').length,
  };

  const handleFixException = (exception, category) => {
    if (!exception) return;
    const key = exception.staffId ?? exception.empId;
    setExceptions((prev) => prev.filter((e) => {
      const sameEmployee = (e.staffId ?? e.empId) === key;
      const sameCategory = getExceptionCategory(e) === category;
      const sameDate = exception.date && e.date ? e.date === exception.date : true;
      return !(sameEmployee && sameCategory && sameDate);
    }));

    setInputEmployees((prev) => prev.map((emp) => {
      const matches = (emp.staffId ?? emp.empId) === key;
      if (!matches) return emp;
      if (category === 'missing') {
        const present = Math.max(0, parseNumber(emp.present, 0) + 1);
        const lop = Math.max(0, parseNumber(emp.lop, 0) - 1);
        const payable = Math.round((parseNumber(emp.payable, 0) + 1) * 100) / 100;
        return { ...emp, present, lop, payable };
      }
      return emp;
    }));

    setAggregates((prev) => {
      if (!prev) return prev;
      const updated = { ...prev };
      updated.needsImmediateAction = Math.max(0, (prev.needsImmediateAction ?? exceptions.length) - 1);
      if (category === 'missing') {
        updated.present = {
          ...prev.present,
          count: Math.max(0, (prev.present?.count ?? 0) + 1),
        };
        updated.lopDays = {
          ...prev.lopDays,
          total: Math.max(0, (prev.lopDays?.total ?? 0) - 1),
        };
        updated.payableDays = {
          ...prev.payableDays,
          total: Math.round(((prev.payableDays?.total ?? 0) + 1) * 100) / 100,
        };
      }
      return updated;
    });

    showToast(`Fixed missing punch for ${exception.name}`);
    setExceptionModal(null);
  };

  // ── Dept chart employees ──
  const getDeptEmployees = (dept) => sourceEmployees.filter(e => e.dept === dept);

  // ── Trend modal employees ──
  const getTrendEmployees = () => sourceEmployees.slice(0, 8);

  // ── Bulk import handler ──
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    showToast("File upload is not yet connected to backend parsing.", "warning");
    e.target.value = '';
  };

  const handleRunPayroll = useCallback(() => {
    if (!procEmployees.length) {
      showToast("No employees to review for the current filters", "error");
      return;
    }
    setRunPayrollLoading(true);
    const employeeIds = procEmployees
      .map(e => Number(e.staffId ?? e.empId))
      .filter(id => id && !Number.isNaN(id));

    payrollActionRef.current = "runPayroll";
    dispatch(commonSaveData({
      path: runPayrollPath,
      data: {
        userId: String(user.user_id),
        selectedMonth,
        employeeIds,
        action: "preview",
      },
    }));
  }, [dispatch, user.user_id, selectedMonth, procEmployees]);

  // ── KPI cards built from API data ──
  const kpis = [
    { icon: "fa-solid fa-calendar-check", iconColor: C.accent,   label: "Total Days",   value: totalDays,        tag: "Month",                                                tagColor: C.muted    },
    { icon: "fa-solid fa-user-check",      iconColor: C.success,  label: "Present",      value: presentCount,     tag: `${presentPct}%`,                                       tagColor: C.success  },
    { icon: "fa-solid fa-user-xmark",      iconColor: C.error,    label: "LOP Days",     value: lopTotal,         tag: lopChange >= 0 ? `+${lopChange}` : `${lopChange}`,      tagColor: C.error    },
    { icon: "fa-solid fa-clock",           iconColor: C.warning,  label: "OT Hours",     value: otTotal,          tag: otChange >= 0 ? `+${otChange}` : `${otChange}`,         tagColor: C.warning  },
    { icon: "fa-solid fa-calendar-days",   iconColor: "#22D3EE",  label: "Payable Days", value: payableDaysTotal, tag: payableStatus,                                          tagColor: payableStatus === "Ready" ? C.success : C.warning },
    { icon: "fa-solid fa-triangle-exclamation", iconColor: C.error, label: "Pending",   value: pendingCount,     tag: "Action",                                               tagColor: C.error, glow: "rgba(239,68,68,0.2)" },
  ];
  return (
    <div style={{ maxWidth: 1500, margin: "0 auto", padding: "24px 20px", display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <h1 style={{fontSize: 12, fontWeight: 700, margin: 0, color: "var(--text-primary)" }}>Attendance Inputs</h1>
            <span style={{ padding: "3px 10px", borderRadius: 16, background: "rgba(206,203,212,0.15)", color: C.accent, border: `1px solid rgba(134,96,248,0.3)`, fontSize: 10 }}>{formatPreviewMonthLabel(selectedMonth)}</span>
            {inputsLoading && <span style={{ fontSize: 10, color: C.muted }}>Loading…</span>}
          </div>
          <p style={{ color: "var(--text-muted)", margin: 0, fontSize: 12 }}>Verify payable days, LOP & OT before running payroll for {procEmployees.length} employees</p>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button style={{ ...s.btnGhost, padding: "6px 12px",fontSize: 12 }} onClick={() => fileInputRef.current?.click()}><i className="fa-solid fa-file-import" style={{fontSize: 12 }} />Import Corrections</button>
          <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={handleFileUpload} />
          <button style={{ ...s.btnGhost, padding: "6px 12px",fontSize: 12 }}><i className="fa-solid fa-file-excel" style={{fontSize: 12 }} />Export Excel</button>
          <button style={{ ...s.btnSecondary, padding: "6px 14px",fontSize: 12 }}><i className="fa-solid fa-calculator" style={{fontSize: 12 }} />Recalculate</button>
          <button
            style={{
              ...s.btnSecondary,
              padding: "6px 14px",
              fontSize: 12,
              color: isLocked ? '#4ADE80' : 'var(--pay-text, #e2dff7)',
              borderColor: isLocked ? 'rgba(74,222,128,0.4)' : 'rgba(134,96,248,0.3)',
              background: isLocked ? 'rgba(74,222,128,0.08)' : 'rgba(134,96,248,0.08)',
            }}
            onClick={() => isLocked ? setShowUnlockConfirm(true) : setShowLockConfirm(true)}
            disabled={lockLoading}
          >
            <i className={`fa-solid ${isLocked ? 'fa-lock' : 'fa-lock-open'}`} style={{ fontSize: 12, color: isLocked ? '#4ADE80' : 'var(--pay-text, #e2dff7)' }} />
            {lockLoading ? 'Updating…' : isLocked ? 'Locked' : 'Lock Attendance'}
          </button>
          <button style={{ ...s.btnSecondary, padding: "6px 14px",fontSize: 12 }} onClick={handleAutoResolve}><i className="fa-solid fa-wand-magic-sparkles" style={{fontSize: 12 }} />Auto Resolve</button>
          <button style={{ ...s.btnPrimary, padding: "8px 18px", fontSize: 12, animation: "pulse-glow 2s ease-in-out infinite" }} onClick={handleRunPayroll} disabled={runPayrollLoading}><i className="fa-solid fa-arrow-right" style={{fontSize: 12 }} />{runPayrollLoading ? 'Opening...' : 'Approval Queue'}</button>
        </div>
      </div>
      <div className="filter-controls" style={{ background: "var(--pay-surface)", border: "1px solid var(--pay-border)", borderRadius: 12, padding: "8px 12px", marginBottom: 0, flexWrap: "nowrap", overflowX: "auto", display: "flex", alignItems: "center", gap: 8 }}>

          {/* Month */}
          <div style={{ flex: "0 0 auto", minWidth: 0 }}>
            <MonthPicker
              value={selectedMonth}
              onChange={(val) => {
                setSelectedMonth(val);
                fetchAggregates(val);
              }}
            />
          </div>

          {/* Branch */}
          <div className="field-wrap" style={{ flex: "0 0 130px", minWidth: 0 }}>
            <select className="filter-select" value={selectedBranch} onChange={e => setSelectedBranch(e.target.value)} style={{ width: "100%", fontSize: 11, padding: "6px 24px 6px 10px" }}>
              <option value="">All Branches</option>
              {branchList.map(b => <option key={b.branchId} value={b.branchId}>{b.branch_name}</option>)}
            </select>
            <span className="fi-right">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
            </span>
          </div>

          {/* Department */}
          <div className="field-wrap" style={{ flex: "0 0 145px", minWidth: 0 }}>
            <select className="filter-select" value={selectedDept} onChange={e => setSelectedDept(e.target.value)} style={{ width: "100%", fontSize: 11, padding: "6px 24px 6px 10px" }}>
              <option value="">All Departments</option>
              {departmentList.map(d => <option key={d.departmentId} value={d.departmentId}>{d.department_name}</option>)}
            </select>
            <span className="fi-right">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
            </span>
          </div>

          {/* Shift */}
          <div className="field-wrap" style={{ flex: "0 0 120px", minWidth: 0 }}>
            <select className="filter-select" value={selectedShift} onChange={e => setSelectedShift(e.target.value)} style={{ width: "100%", fontSize: 11, padding: "6px 24px 6px 10px" }}>
              <option value="">All Shifts</option>
              {shiftList.map(sh => <option key={sh.shiftId} value={sh.shiftId}>{sh.shiftName}</option>)}
            </select>
            <span className="fi-right">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
            </span>
          </div>

          {/* Type */}
          <div className="field-wrap" style={{ flex: "0 0 115px", minWidth: 0 }}>
            <select className="filter-select" value={selectedType} onChange={e => setSelectedType(e.target.value)} style={{ width: "100%", fontSize: 11, padding: "6px 24px 6px 10px" }}>
              <option value="">All Types</option>
              <option value="System">System</option>
              <option value="Manager">Manager</option>
              <option value="Manual">Manual</option>
              <option value="Corrected">Corrected</option>
            </select>
            <span className="fi-right">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
            </span>
          </div>

          {/* Search */}
          <div className="field-wrap search-input-wrapper" style={{ flex: "1 1 180px", minWidth: 0 }}>
            <input className="filter-select" placeholder="Search by Employee ID or Name" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ width: "100%", fontSize: 11, padding: "6px 12px", cursor: "text" }} />
          </div>

      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10 }}>
        {kpis.map(k => <KpiCard key={k.label} {...k} />)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
        <ApprovalKpiCard icon="fa-circle-exclamation" iconColor={C.error}   borderColor={C.error}   value={needsActionCount} label="Needs Immediate Action" />
        <ApprovalKpiCard icon="fa-clock"              iconColor={C.warning} borderColor={C.warning} value={pendingCount}     label="Total Pending Cases" />
        <ApprovalKpiCard icon="fa-check-double"       iconColor={C.success} borderColor={C.success} value={approvedCount}    label="Approved This Month" glow="rgba(74,222,128,0.2)" />
      </div>
      <div style={{ ...s.card, padding: 16, borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 12 }}>Employee Attendance List</div>
            <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>{procEmployees.length} employees loaded for {formatPreviewMonthLabel(selectedMonth)}</div>
          </div>
          <button
            style={{ ...s.btnGhost, padding: '5px 12px', fontSize: 10, opacity: selectedEmp ? 1 : 0.45, cursor: selectedEmp ? 'pointer' : 'not-allowed' }}
            onClick={() => { if (selectedEmp) setDrawerOpen(true); }}
          >
            {selectedEmp ? `View: ${selectedEmp.name}` : 'Select an employee'}
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
            <thead>
              <tr style={{ background: C.elevated }}>
                <th style={{ padding: '10px 14px', width: 44, borderBottom: `1px solid ${C.border}` }} />
                {['Employee', 'Present', 'LOP', 'OT', 'Payable', 'Source', 'Status'].map((col) => (
                  <th key={col} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: C.muted, borderBottom: `1px solid ${C.border}` }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {procEmployees.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 16, textAlign: 'center', color: C.muted, fontSize: 12 }}>No employee data available for the selected filters.</td></tr>
              ) : procEmployees.slice(0, 12).map((emp, index) => {
                const isSelected = selectedEmp?.staffId
                  ? selectedEmp.staffId === emp.staffId
                  : selectedEmp?.empId === emp.empId;
                return (
                  <tr
                    key={`${emp.empId}-${index}`}
                    onClick={() => {
                      setSelectedEmp(isSelected ? null : emp);
                    }}
                    style={{
                      borderBottom: `1px solid rgba(169,163,201,0.08)`,
                      background: isSelected ? 'rgba(134,96,248,0.08)' : 'transparent',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                  >
                    {/* Checkbox */}
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 16, height: 16, borderRadius: 4,
                        border: isSelected ? '2px solid #8660F8' : '2px solid rgba(134,96,248,0.35)',
                        background: isSelected ? '#8660F8' : 'rgba(134,96,248,0.05)',
                        transition: 'all 0.15s ease', flexShrink: 0,
                      }}>
                        {isSelected && (
                          <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                            <path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </span>
                    </td>
                    {/* Employee */}
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <img src={emp.avatar} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: isSelected ? '2px solid #8660F8' : '2px solid transparent', transition: 'border 0.15s' }} />
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600 }}>{emp.name}</div>
                          <div style={{ fontSize: 10, color: C.muted }}>{emp.empId}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 11, fontWeight: 600, color: C.success }}>{emp.present}</td>
                    <td style={{ padding: '10px 14px', fontSize: 11, fontWeight: 600, color: C.error }}>{emp.lop}</td>
                    <td style={{ padding: '10px 14px', fontSize: 11, fontWeight: 600, color: C.warning }}>{Number(emp.ot ?? 0).toFixed(1)}</td>
                    <td style={{ padding: '10px 14px', fontSize: 11, fontWeight: 600 }}>{emp.payable}</td>
                    <td style={{ padding: '10px 14px', fontSize: 11 }}>
                      <span style={{ display: 'inline-flex', padding: '3px 8px', borderRadius: 16, background: `${emp.sourceColor ?? C.accent}18`, color: emp.sourceColor ?? C.accent, border: `1px solid ${emp.sourceColor ?? C.accent}40`, fontSize: 10 }}>{emp.source}</span>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 11 }}><StatusChip status={emp.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ ...s.card, padding: 16, borderLeft: `4px solid ${C.error}`, boxShadow: "0 0 16px rgba(239,68,68,0.15)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <div style={{ fontWeight: 600,fontSize: 12 }}>Exception Center</div>
              <span style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(239,68,68,0.2)", color: C.error, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>{needsActionCount}</span>
            </div>
            <div style={{ color: C.muted, fontSize: 10 }}>Attendance anomalies blocking payroll - immediate action required</div>
          </div>
          <button style={{ ...s.btnGhost, padding: "4px 10px", fontSize: 10 }}>Resolve All</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          {[
            ["fa-user-clock",      C.error,   "Missing Punch Records", `${exceptionCounts.missing} employees`, "missing"],
            ["fa-clock-rotate-left",C.warning, "Excess OT Hours",       `${exceptionCounts.ot} employees`, "ot"],
            ["fa-ban",             C.error,   "Unapproved Absences",   `${exceptionCounts.absences} employees`, "absences"],
          ].map(([icon,color,title,sub,type]) => (
            <div key={title} style={{ ...s.elevated, padding: 12, borderLeft: `2px solid ${color}`, borderRadius: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <i className={`fa-solid ${icon}`} style={{ color,fontSize: 12 }} />
                </div>
                <div>
                  <div style={{ fontWeight: 600,fontSize: 12 }}>{title}</div>
                  <div style={{ fontSize: 9, color: C.muted }}>{sub}</div>
                </div>
              </div>
              <button onClick={() => setExceptionModal(type)} style={{ ...s.btnSecondary, width: "100%", justifyContent: "center", padding: "5px 10px", fontSize: 10 }}>Review & Fix</button>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={{ ...s.card, padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div><div style={{ fontWeight: 600,fontSize: 12 }}>Department-wise Payable Days</div><div style={{ color: C.muted, fontSize: 10, marginTop: 2 }}>Average payable days by department — click a bar to see employees</div></div>
            <button style={{ ...s.btnGhost, padding: "4px 10px", fontSize: 10 }}>View Details</button>
          </div>
          <PlotlyBarChart departments={deptChartDepts} avgPayableDays={deptChartVals} onBarClick={(dept) => setDeptModal(dept)} />
        </div>
        <div style={{ ...s.card, padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div><div style={{ fontWeight: 600,fontSize: 12 }}>LOP & OT Trends</div><div style={{ color: C.muted, fontSize: 10, marginTop: 2 }}>Click a point to see employee details</div></div>
            <button style={{ ...s.btnGhost, padding: "4px 10px", fontSize: 10 }}>Export Data</button>
          </div>
          <PlotlyLineChart months={trendMonths} lopDays={trendLop} otHours={trendOt} onPointClick={(month) => setTrendModal(month)} />
        </div>
      </div>
      <div style={{ ...s.card, padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div><div style={{ fontWeight: 600,fontSize: 12 }}>Bulk Import Corrections</div><div style={{ color: C.muted, fontSize: 10, marginTop: 2 }}>Upload attendance corrections via CSV or Excel file</div></div>
          <button style={{ ...s.btnGhost, padding: "4px 10px", fontSize: 10 }}><i className="fa-solid fa-download" style={{ fontSize: 10 }} />Download Template</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          {[["fa-file-csv",C.accent,"Upload CSV File"],["fa-file-excel",C.success,"Upload Excel File"]].map(([icon,color,label]) => (
            <div key={label} style={{ ...s.elevated, padding: 16, borderRadius: 12, border: `2px dashed rgba(169,163,201,0.2)`, textAlign: "center", cursor: "pointer" }}>
              <div style={{ width: 44, height: 44, borderRadius: "50%", background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
                <i className={`fa-solid ${icon}`} style={{ color,fontSize: 12 }} />
              </div>
              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 10, color: C.muted, marginBottom: 10 }}>Drag and drop or click to browse</div>
              <button style={{ ...s.btnSecondary, fontSize: 10, padding: "5px 14px", margin: "0 auto" }}>Choose File</button>
            </div>
          ))}
        </div>
        <div style={{ ...s.elevated, padding: 10, borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}>
          <i className="fa-solid fa-circle-info" style={{ color: C.accent, fontSize: 12 }} />
          <span style={{ fontSize: 10, color: C.muted }}>File must contain columns: Employee ID, Date, LOP Days, OT Hours, Remarks. Maximum 5000 rows per upload.</span>
        </div>
      </div>
      <div style={{ ...s.card, padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div><div style={{ fontWeight: 600,fontSize: 12 }}>Attendance Health Check</div><div style={{ color: C.muted, fontSize: 10, marginTop: 2 }}>Validation status before payroll processing</div></div>
          <span style={{ color: C.success, fontSize: 10, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}><i className="fa-solid fa-check-circle" style={{ fontSize: 10 }} />4 of 5 Complete</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
          {[{label:"Data Locked",sub:"Attendance frozen",ok:true},{label:"Shifts Assigned",sub:"All mapped",ok:true},{label:"Leave Applied",sub:"Approved leaves",ok:true},{label:"OT Approved",sub:"Manager verified",ok:true},{label:"Exceptions",sub:"18 pending",ok:false}].map(item => (
            <div key={item.label} style={{ ...s.elevated, padding: 10, borderLeft: `3px solid ${item.ok ? C.success : C.warning}`, boxShadow: item.ok ? "none" : `0 0 10px rgba(245,158,11,0.15)` }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: item.ok ? "rgba(74,222,128,0.1)" : "rgba(245,158,11,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className={item.ok ? "fa-solid fa-check" : "fa-solid fa-clock"} style={{ color: item.ok ? C.success : C.warning, fontSize: 12 }} />
                </div>
                <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 12, background: item.ok ? "rgba(74,222,128,0.1)" : "rgba(245,158,11,0.1)", color: item.ok ? C.success : C.warning, border: `1px solid ${item.ok ? "rgba(74,222,128,0.2)" : "rgba(245,158,11,0.2)"}` }}>{item.ok ? "Ready" : "Pending"}</span>
              </div>
              <div style={{ fontWeight: 600,fontSize: 12 }}>{item.label}</div>
              <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>{item.sub}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ ...s.card, padding: 14, borderLeft: `3px solid ${C.accent}`, boxShadow: "0 0 16px rgba(134,96,248,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(134,96,248,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="fa-solid fa-clipboard-check" style={{ color: C.accent,fontSize: 12 }} />
            </div>
            <div>
              <div style={{ fontWeight: 700,fontSize: 12, marginBottom: 2 }}>{presentCount} of {totalDays} Days Recorded</div>
              <div style={{ fontSize: 10, color: C.muted }}>{pendingCount} employees pending review · Total Payable: {payableDaysTotal} days</div>
              <div style={{ display: "flex", gap: 12, marginTop: 4, fontSize: 10 }}>
                <span style={{ color: C.success }}><i className="fa-solid fa-check-circle" style={{ fontSize: 9 }} /> {approvedCount} Approved</span>
                <span style={{ color: C.warning }}><i className="fa-solid fa-clock" style={{ fontSize: 9 }} /> {pendingCount} Pending</span>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={{ ...s.btnSecondary, padding: "5px 12px", fontSize: 10 }}><i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 9 }} />Resolve Pending</button>
            <button style={{ ...s.btnPrimary, padding: "6px 16px",fontSize: 12 }} onClick={handleRunPayroll} disabled={runPayrollLoading}><i className="fa-solid fa-arrow-right" style={{ fontSize: 10 }} />{runPayrollLoading ? 'Running...' : 'Approval Queue'}</button>
          </div>
        </div>
      </div>
      <DrawerPanel open={drawerOpen} onClose={() => setDrawerOpen(false)} employee={selectedEmp} />
      <style>{`@keyframes pulse-glow { 0%,100% { box-shadow: 0 0 10px rgba(134,96,248,0.3); } 50% { box-shadow: 0 0 18px rgba(134,96,248,0.5); } }`}</style>

      {/* ── Toast ── */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 99999, background: toast.type === 'success' ? 'rgba(74,222,128,0.15)' : 'rgba(239,68,68,0.15)', border: `1px solid ${toast.type === 'success' ? 'rgba(74,222,128,0.4)' : 'rgba(239,68,68,0.4)'}`, borderRadius: 10, padding: '12px 20px', color: toast.type === 'success' ? '#4ADE80' : '#EF4444', fontSize: 13, fontWeight: 500, backdropFilter: 'blur(8px)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className={`fa-solid ${toast.type === 'success' ? 'fa-circle-check' : 'fa-circle-xmark'}`} />
          {toast.msg}
        </div>
      )}

      {/* ── Lock Confirm Modal ── */}
      {showLockConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowLockConfirm(false)}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 28, maxWidth: 400, width: '90%', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="fa-solid fa-lock" style={{ color: C.warning, fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>Lock Attendance</div>
                <div style={{ fontSize: 11, color: C.muted }}>This action cannot be undone</div>
              </div>
            </div>
            <p style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>Are you sure you want to lock attendance for <strong style={{ color: 'var(--text-primary)' }}>{selectedMonth}</strong>? No further modifications will be allowed.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button style={{ ...s.btnGhost, padding: '7px 16px', fontSize: 12 }} onClick={() => setShowLockConfirm(false)}>Cancel</button>
              <button style={{ ...s.btnPrimary, padding: '7px 16px', fontSize: 12, background: C.warning, boxShadow: 'none' }} onClick={handleLockConfirm}>
                <i className="fa-solid fa-lock" /> Lock Attendance
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Unlock Confirm Modal ── */}
      {showUnlockConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowUnlockConfirm(false)}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 28, maxWidth: 400, width: '90%', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="fa-solid fa-lock-open" style={{ color: C.error, fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>Unlock Attendance</div>
                <div style={{ fontSize: 11, color: C.muted }}>This will allow modifications again</div>
              </div>
            </div>
            <p style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>Are you sure you want to unlock attendance for <strong style={{ color: 'var(--text-primary)' }}>{selectedMonth}</strong>? Employees will be able to make changes.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button style={{ ...s.btnGhost, padding: '7px 16px', fontSize: 12 }} onClick={() => setShowUnlockConfirm(false)}>Cancel</button>
              <button style={{ ...s.btnPrimary, padding: '7px 16px', fontSize: 12, background: C.error, boxShadow: 'none' }} onClick={handleUnlockConfirm}>
                <i className="fa-solid fa-lock-open" /> Unlock Attendance
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Exception Review & Fix Modal ── */}
      {exceptionModal && (() => {
        const titles = { missing: 'Missing Punch Records', ot: 'Excess OT Hours', absences: 'Unapproved Absences' };
        const emps = getExceptionEmployees(exceptionModal);
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setExceptionModal(null)}>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 0, maxWidth: 680, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{titles[exceptionModal]}</div>
                <button onClick={() => setExceptionModal(null)} style={{ ...s.elevated, width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}><i className="fa-solid fa-xmark" /></button>
              </div>
              <div style={{ overflowY: 'auto', padding: '16px 24px', flex: 1 }}>
                {emps.map((emp, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid rgba(169,163,201,0.08)' }}>
                    <img src={emp.avatar} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} alt="" />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 12 }}>{emp.name}</div>
                      <div style={{ fontSize: 10, color: C.muted }}>{emp.dept} · {emp.empId}</div>
                    </div>
                    <div style={{ fontSize: 10, color: C.muted, minWidth: 80 }}>
                      {exceptionModal === 'missing' && `LOP: ${emp.lop} days`}
                      {exceptionModal === 'ot'      && `OT: ${emp.ot} hrs`}
                      {exceptionModal === 'absences'&& `Status: ${emp.status}`}
                    </div>
                    <button onClick={() => handleFixException(emp, exceptionModal)} style={{ ...s.btnPrimary, padding: '5px 12px', fontSize: 10, boxShadow: 'none' }}>Fix</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Department Chart Modal ── */}
      {deptModal && (() => {
        const emps = getDeptEmployees(deptModal);
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setDeptModal(null)}>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, maxWidth: 640, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{deptModal} — Payable Days</div>
                <button onClick={() => setDeptModal(null)} style={{ ...s.elevated, width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}><i className="fa-solid fa-xmark" /></button>
              </div>
              <div style={{ overflowY: 'auto', padding: '16px 24px', flex: 1 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead><tr style={{ background: C.elevated }}>{['Employee','LOP Days','OT Hours','Payable Days'].map(h => <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: C.muted }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {emps.map((emp, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(169,163,201,0.06)' }}>
                        <td style={{ padding: '8px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <img src={emp.avatar} style={{ width: 28, height: 28, borderRadius: '50%' }} alt="" />
                            <div><div style={{ fontWeight: 600, fontSize: 11 }}>{emp.name}</div><div style={{ fontSize: 9, color: C.muted }}>{emp.empId}</div></div>
                          </div>
                        </td>
                        <td style={{ padding: '8px 12px', color: C.error, fontWeight: 600, fontSize: 11 }}>{emp.lop}</td>
                        <td style={{ padding: '8px 12px', color: '#60A5FA', fontWeight: 600, fontSize: 11 }}>{emp.ot}</td>
                        <td style={{ padding: '8px 12px', fontWeight: 700, fontSize: 11 }}>{emp.payable}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Trend Modal ── */}
      {trendModal && (() => {
        const emps = getTrendEmployees();
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setTrendModal(null)}>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, maxWidth: 680, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>LOP & OT Details — {trendModal}</div>
                <button onClick={() => setTrendModal(null)} style={{ ...s.elevated, width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}><i className="fa-solid fa-xmark" /></button>
              </div>
              <div style={{ overflowY: 'auto', padding: '16px 24px', flex: 1 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead><tr style={{ background: C.elevated }}>{['Employee','Department','LOP Days','OT Hours'].map(h => <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: C.muted }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {emps.map((emp, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(169,163,201,0.06)' }}>
                        <td style={{ padding: '8px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <img src={emp.avatar} style={{ width: 28, height: 28, borderRadius: '50%' }} alt="" />
                            <div><div style={{ fontWeight: 600, fontSize: 11 }}>{emp.name}</div><div style={{ fontSize: 9, color: C.muted }}>{emp.empId}</div></div>
                          </div>
                        </td>
                        <td style={{ padding: '8px 12px', color: C.muted, fontSize: 11 }}>{emp.dept}</td>
                        <td style={{ padding: '8px 12px', color: C.error, fontWeight: 600, fontSize: 11 }}>{emp.lop}</td>
                        <td style={{ padding: '8px 12px', color: '#60A5FA', fontWeight: 600, fontSize: 11 }}>{emp.ot}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PAGE 2 — DETAIL REVIEW  (unchanged — all original code preserved)
// ════════════════════════════════════════════════════════════════════════════
const dayData = [
  { d: "01", type: "present" }, { d: "02", type: "present" }, { d: "03", type: "late"    },
  { d: "04", type: "holiday" }, { d: "05", type: "weekoff" }, { d: "06", type: "weekoff" },
  { d: "07", type: "present" }, { d: "08", type: "missing" }, { d: "09", type: "present" },
  { d: "10", type: "leave"   }, { d: "11", type: "weekoff" }, { d: "12", type: "weekoff" },
  { d: "14", type: "present" }, { d: "15", type: "present" }, { d: "16", type: "present" },
  { d: "17", type: "present" }, { d: "18", type: "late"    }, { d: "19", type: "weekoff" },
  { d: "20", type: "weekoff" }, { d: "21", type: "present" }, { d: "22", type: "present" },
  { d: "23", type: "lop"     }, { d: "24", type: "present" }, { d: "25", type: "present" },
  { d: "26", type: "weekoff" }, { d: "27", type: "weekoff" }, { d: "28", type: "present" },
  { d: "29", type: "present" }, { d: "30", type: "present" },
];
const dayColors = {
  present: { bg: "rgba(74,222,128,0.15)",  color: C.success, border: "rgba(74,222,128,0.3)"  },
  absent:  { bg: "rgba(239,68,68,0.15)",   color: C.error,   border: "rgba(239,68,68,0.3)"   },
  weekoff: { bg: "rgba(169,163,201,0.1)",  color: C.muted,   border: "rgba(169,163,201,0.2)" },
  holiday: { bg: "rgba(59,130,246,0.15)",  color: "#3B82F6", border: "rgba(59,130,246,0.3)"  },
  leave:   { bg: "rgba(168,85,247,0.15)",  color: "#A855F7", border: "rgba(168,85,247,0.3)"  },
  lop:     { bg: "rgba(245,158,11,0.15)",  color: C.warning, border: "rgba(245,158,11,0.3)"  },
  missing: { bg: "transparent",            color: C.error,   border: C.error, dashed: true   },
  late:    { bg: "rgba(245,158,11,0.1)",   color: C.warning, border: "rgba(245,158,11,0.25)" },
  half:    { bg: "rgba(168,85,247,0.1)",   color: "#A855F7", border: "rgba(168,85,247,0.25)" },
};

// Per-day punch details — keyed by day number string
const DAY_DETAILS = {
  "01": { in: "09:02 AM", out: "06:15 PM", worked: "9h 13m", note: null,          badge: null },
  "02": { in: "09:10 AM", out: "06:30 PM", worked: "9h 20m", note: null,          badge: null },
  "03": { in: "09:42 AM", out: "06:45 PM", worked: "9h 03m", note: "Late 42 min", badge: "Late Mark" },
  "04": { in: "—",        out: "—",        worked: "—",       note: "Public Holiday", badge: "Holiday" },
  "05": { in: "—",        out: "—",        worked: "—",       note: "Weekly Off",  badge: "Week Off" },
  "06": { in: "—",        out: "—",        worked: "—",       note: "Weekly Off",  badge: "Week Off" },
  "07": { in: "09:05 AM", out: "06:20 PM", worked: "9h 15m", note: null,          badge: null },
  "08": { in: "09:18 AM", out: "—",        worked: "—",       note: "Missing punch out", badge: "Missing Punch" },
  "09": { in: "09:00 AM", out: "06:00 PM", worked: "9h 00m", note: null,          badge: null },
  "10": { in: "—",        out: "—",        worked: "—",       note: "Approved Leave", badge: "Leave" },
  "11": { in: "—",        out: "—",        worked: "—",       note: "Weekly Off",  badge: "Week Off" },
  "12": { in: "—",        out: "—",        worked: "—",       note: "Weekly Off",  badge: "Week Off" },
  "14": { in: "09:08 AM", out: "06:25 PM", worked: "9h 17m", note: null,          badge: null },
  "15": { in: "09:01 AM", out: "06:10 PM", worked: "9h 09m", note: null,          badge: null },
  "16": { in: "09:03 AM", out: "06:18 PM", worked: "9h 15m", note: null,          badge: null },
  "17": { in: "09:00 AM", out: "06:00 PM", worked: "9h 00m", note: null,          badge: null },
  "18": { in: "09:50 AM", out: "07:10 PM", worked: "9h 20m", note: "Late 50 min", badge: "Late Mark" },
  "19": { in: "—",        out: "—",        worked: "—",       note: "Weekly Off",  badge: "Week Off" },
  "20": { in: "—",        out: "—",        worked: "—",       note: "Weekly Off",  badge: "Week Off" },
  "21": { in: "09:05 AM", out: "06:15 PM", worked: "9h 10m", note: null,          badge: null },
  "22": { in: "09:02 AM", out: "06:20 PM", worked: "9h 18m", note: null,          badge: null },
  "23": { in: "—",        out: "—",        worked: "—",       note: "LOP — Unapproved absence", badge: "LOP" },
  "24": { in: "09:00 AM", out: "06:00 PM", worked: "9h 00m", note: null,          badge: null },
  "25": { in: "09:07 AM", out: "06:22 PM", worked: "9h 15m", note: null,          badge: null },
  "26": { in: "—",        out: "—",        worked: "—",       note: "Weekly Off",  badge: "Week Off" },
  "27": { in: "—",        out: "—",        worked: "—",       note: "Weekly Off",  badge: "Week Off" },
  "28": { in: "09:04 AM", out: "06:18 PM", worked: "9h 14m", note: null,          badge: null },
  "29": { in: "09:01 AM", out: "06:05 PM", worked: "9h 04m", note: null,          badge: null },
  "30": { in: "09:00 AM", out: "06:00 PM", worked: "9h 00m", note: null,          badge: null },
};

const WEEKDAYS = ["","Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const getDayName = (d) => {
  // April 2026: 1st is Wednesday (day 3)
  const dayNum = parseInt(d);
  const weekday = ((dayNum + 1) % 7); // offset for April 2026
  return WEEKDAYS[weekday] || "";
};

const DetailReview = ({ payrollPreview }) => {
  const dispatch    = useDispatch();
  const commonStore = useSelector(s => s.common);
  const user        = JSON.parse(localStorage.getItem("user_data")) || {};

  const [selectedDay,      setSelectedDay]      = useState("08");
  const [empIndex,         setEmpIndex]         = useState(0);
  const [empStatuses,      setEmpStatuses]      = useState({});
  const [currentCompleted, setCurrentCompleted] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [drToast,          setDrToast]          = useState(null);
  const [lopAdj,           setLopAdj]           = useState("0");
  const [otAdj,            setOtAdj]            = useState("0");
  const [payAdj,           setPayAdj]           = useState("");
  const [actionLoading,    setActionLoading]    = useState(false);
  const [detailOverrides,  setDetailOverrides]  = useState({});
  const [detailLoading,    setDetailLoading]    = useState(false);
  const detailActionRef    = useRef(null);

  const WORK_HOURS_PER_DAY = 8;
  const computeCountsFromDayWise = (dayWise = []) => {
    let present = 0, lop = 0, half = 0, paidLeave = 0, holidays = 0, ot = 0, absent = 0;
    if (!Array.isArray(dayWise)) return { present, lop, half, paidLeave, holidays, ot, absent };
    dayWise.forEach(d => {
      const t = normalizeDayStatus(d.status || d.type || d.dayStatus || d.type);
      if (t === 'present') present += 1;
      else if (t === 'half') { half += 1; present += 0.5; }
      else if (t === 'lop') { lop += 1; absent += 1; }
      else if (t === 'absent') { lop += 1; absent += 1; }
      else if (t === 'leave' || /paid/i.test(t)) { paidLeave += 1; }
      else if (t === 'holiday') { holidays += 1; }
      ot += parseNumber(d.ot ?? d.otHours ?? d.overtimeHours ?? 0);
    });
    return { present, lop, half, paidLeave, holidays, ot, absent };
  };

  const computePayableFromCounts = ({ present = 0, lop = 0, half = 0, paidLeave = 0, holidays = 0, ot = 0 }) => {
    const fromOtDays = Number(ot) / WORK_HOURS_PER_DAY;
    const payable =
      (Number(present) || 0) +
      (Number(paidLeave) || 0) +
      (Number(half) || 0) * 0.5 +
      Number(fromOtDays) +
      (Number(holidays) || 0);
    return Math.round(payable * 100) / 100;
  };

  const baseEmployees = payrollPreview?.employees || [];
  const employees = baseEmployees.map((e) => {
    const ov = detailOverrides[e.staffId];
    return ov ? { ...e, ...ov } : e;
  });
  const monthLabel = formatPreviewMonthLabel(payrollPreview?.selectedMonth);
  const usingApiPreview = Boolean(payrollPreview?.employees?.length);
  const emp        = employees[empIndex] || employees[0];
  const empStatus  = empStatuses[emp?.empId] || emp?.status || "Pending";
  const empDayCounts = computeCountsFromDayWise(emp?.dayWise);
  const computedPayableFromDays = computePayableFromCounts(empDayCounts);

  const refetchDetail = useCallback(() => {
    if (!emp?.staffId || !payrollPreview?.selectedMonth) return;
    detailActionRef.current = "fetchDetail";
    setDetailLoading(true);
    dispatch(commonSaveData({
      path: payrollProcessPath,
      data: {
        action:  "fetchDetail",
        userId:  user.user_id,
        staffId: emp.staffId,
        month:   payrollPreview.selectedMonth,
      },
    }));
  }, [dispatch, emp?.staffId, payrollPreview?.selectedMonth, user.user_id]);

  useEffect(() => {
    refetchDetail();
  }, [refetchDetail]);

  useEffect(() => {
    if (detailActionRef.current === "fetchDetail" && commonStore.commonSuccess && commonStore.commonData) {
      const payload = unwrapPayrollResponse(commonStore.commonData);
      const mapped = mapDetailResponse(payload, emp);
      setDetailOverrides((p) => ({ ...p, [emp.staffId]: mapped }));
      setLopAdj(String(mapped.lop ?? 0));
      setOtAdj(String(mapped.ot ?? 0));
      setPayAdj("");
      detailActionRef.current = null;
      setDetailLoading(false);
      dispatch(initCommonState());
    }
    if (detailActionRef.current === "fetchDetail" && commonStore.commonError) {
      detailActionRef.current = null;
      setDetailLoading(false);
      dispatch(initCommonState());
    }
  }, [commonStore.commonSuccess, commonStore.commonError, commonStore.commonData, emp, dispatch]);

  const mutationActionRef = useRef(null);

  // ── Handle API responses for detail mutations ──
  useEffect(() => {
    if (!actionLoading) return;
    if (commonStore.commonSuccess) {
      drShowToast(commonStore.commonMessage || "Saved successfully");
      setActionLoading(false);
      mutationActionRef.current = null;
      dispatch(initCommonState());
      refetchDetail();
    }
    if (commonStore.commonError) {
      drShowToast(commonStore.commonMessage || "Action failed", "error");
      setActionLoading(false);
      mutationActionRef.current = null;
      dispatch(initCommonState());
    }
  }, [actionLoading, commonStore.commonSuccess, commonStore.commonError, commonStore.commonMessage, dispatch, refetchDetail]);

  // ── Screen 4: Update single attendance day ──
  const handleUpdateDay = (date, status) => {
    if (!emp?.staffId) return;
    // optimistic UI update: modify local detailOverrides and payAdj so cards update immediately
    try {
      const normalizedStatus = normalizeDayStatus(status);
      console.debug && console.debug('handleUpdateDay:start', { staffId: emp.staffId, date, status, normalizedStatus });
      const dayKey = String(date).includes('-') ? String(date).split('-').pop() : String(date).padStart(2, '0');
      const base = detailOverrides[emp.staffId] || emp || {};
      const origDayWise = Array.isArray(base.dayWise) ? base.dayWise : (emp.dayWise || []);
      console.debug && console.debug('handleUpdateDay:origDayWise', { origDayWise });
      let found = false;
      const updatedDayWise = origDayWise.map(d => {
        const dKey = (d.date && d.date.includes('-')) ? String(d.date).split('-').pop() : String(d.d || d.day || d.dayNum || d.dayNumber || '').padStart?.(2,'0') || '';
        if (dKey === dayKey) {
          found = true;
          return { ...d, status: normalizedStatus };
        }
        return d;
      });

      // remove any existing entries for this day (dedupe) and then add exactly one updated entry
      const cleanedDayWise = updatedDayWise.filter(d => {
        const k = (d.date && d.date.includes('-')) ? String(d.date).split('-').pop() : String(d.d || d.day || d.dayNum || d.dayNumber || '').padStart?.(2,'0') || '';
        return k !== dayKey;
      });

      if (found) {
        const updatedEntry = updatedDayWise.find(d => {
          const k = (d.date && d.date.includes('-')) ? String(d.date).split('-').pop() : String(d.d || d.day || d.dayNum || d.dayNumber || '').padStart?.(2,'0') || '';
          return k === dayKey;
        });
        if (updatedEntry) cleanedDayWise.push(updatedEntry);
      } else {
        cleanedDayWise.push({ d: dayKey, date: `${payrollPreview?.selectedMonth || ''}-${dayKey}`, status: normalizedStatus });
      }

      const counts = computeCountsFromDayWise(cleanedDayWise);
      const computedPayable = computePayableFromCounts(counts);
      // debug: log updated daywise and counts to help track unexpected increments
      console.debug && console.debug('handleUpdateDay:updatedDayWise', { cleanedDayWise, counts, computedPayable });
      // check for duplicate entries for same day
      try {
        const keys = updatedDayWise.map(d => (d.date && d.date.includes('-')) ? String(d.date).split('-').pop() : String(d.d || d.day || d.dayNum || d.dayNumber || '').padStart?.(2,'0') || '');
        const dup = keys.filter((k,i)=>k && keys.indexOf(k)!==i);
        if (dup && dup.length) console.debug && console.debug('handleUpdateDay:duplicateDayKeys', { dup });
      } catch (e) {
        /* ignore */
      }

      // apply local override so UI reflects change immediately
      setDetailOverrides(prev => ({
        ...prev,
        [emp.staffId]: {
          ...(prev[emp.staffId] || emp),
          dayWise: cleanedDayWise,
          attendance: { ...(prev[emp.staffId]?.attendance || emp?.attendance || {}), updated: true },
          present: counts.present,
          lop: counts.lop,
          ot: counts.ot,
          payable: computedPayable,
        }
      }));
      // set pay adjustment input so `effectivePayable` shows updated value
      setPayAdj(String(computedPayable));
    } catch (err) {
      // ignore optimistic update errors
    }

    const fullDate =
      date && /^\d{4}-\d{2}-\d{2}$/.test(date)
        ? date
        : `${payrollPreview?.selectedMonth || ""}-${String(date).padStart(2, "0")}`;
    mutationActionRef.current = "updateDay";
    setActionLoading(true);
    dispatch(commonSaveData({
      path: payrollProcessPath,
      data: { action: "updateDay", userId: user.user_id, staffId: emp.staffId, date: fullDate, status: normalizeDayStatus(status) },
    }));
  };

  // ── Screen 5: Save monthly adjustments ──
  const handleSaveAdjustments = () => {
    if (!emp?.staffId) return;
    mutationActionRef.current = "saveAdjustments";
    setActionLoading(true);
    dispatch(commonSaveData({
      path: payrollProcessPath,
      data: {
        action:          "saveAdjustments",
        userId:          user.user_id,
        staffId:         emp.staffId,
        month:           payrollPreview?.selectedMonth || "",
        otHours:         Number(otAdj)  || 0,
        lopDays:         Number(lopAdj) || 0,
        payableOverride: Number(payAdj) || 0,
      },
    }));
    setCurrentCompleted(true);
  };

  // ── Screen 6: Approve attendance ──
  const handleApproveAttendance = () => {
    if (!emp?.staffId) return;
    mutationActionRef.current = "approveAttendance";
    setActionLoading(true);
    dispatch(commonSaveData({
      path: payrollProcessPath,
      data: {
        action:  "approveAttendance",
        userId:  user.user_id,
        staffId: emp.staffId,
        month:   payrollPreview?.selectedMonth || "",
      },
    }));
    setEmpStatuses(p => ({ ...p, [emp.empId]: "Approved" }));
    setCurrentCompleted(true);
  };

  // ── Screen 7: Resolve all issues ──
  const handleResolveIssues = () => {
    if (!emp?.staffId) return;
    mutationActionRef.current = "resolveIssues";
    setActionLoading(true);
    dispatch(commonSaveData({
      path: payrollProcessPath,
      data: {
        action:  "resolveIssues",
        userId:  user.user_id,
        staffId: emp.staffId,
        month:   payrollPreview?.selectedMonth || "",
      },
    }));
    setCurrentCompleted(true);
  };

  useEffect(() => {
    setEmpIndex(0);
    setCurrentCompleted(false);
    setSelectedDay("08");
  }, [payrollPreview?.selectedMonth, payrollPreview?.employees?.length]);

  useEffect(() => {
    if (!emp) return;
    setLopAdj(String(emp.lop ?? 0));
    setOtAdj(String(emp.ot ?? 0));
    setPayAdj("");
  }, [empIndex, emp?.empId, emp?.lop, emp?.ot, emp?.payable]);

  const drShowToast = (msg, type = 'success') => {
    setDrToast({ msg, type });
    setTimeout(() => setDrToast(null), 3000);
  };

  const handleHold = () => {
    setEmpStatuses(p => ({ ...p, [emp.empId]: 'Hold' }));
    setCurrentCompleted(true);
    drShowToast(`${emp.name} marked as Hold`);
  };

  const handleSaveChanges = () => {
    handleSaveAdjustments();
    drShowToast(`Changes saved for ${emp.name}`);
  };

  const handleApprove = () => {
    handleApproveAttendance();
    drShowToast(`Attendance approved for ${emp.name}`);
  };

  const handleNext = () => {
    if (!currentCompleted) {
      drShowToast('Please save changes or approve attendance before moving to next employee', 'error');
      return;
    }
    const next = (empIndex + 1) % employees.length;
    setEmpIndex(next);
    setCurrentCompleted(false);
    setSelectedDay("08");
  };

  const calendar = buildCalendarFromDayWise(emp?.dayWise, payrollPreview?.selectedMonth);
  const activeDayData = calendar.days.length ? calendar.days : [];
  const activeDayDetails = calendar.details;

  useEffect(() => {
    if (activeDayData.length && !activeDayData.find((d) => d.d === selectedDay)) {
      setSelectedDay(activeDayData[0].d);
    }
  }, [emp?.staffId, activeDayData.length]); // eslint-disable-line

  const selDayData = activeDayData.find((d) => d.d === selectedDay) || activeDayData[0] || { d: selectedDay, type: "present" };
  const selDayColor = dayColors[selDayData?.type] || dayColors.present;
  const effectivePayable = payAdj !== "" && payAdj != null
    ? Number(payAdj)
    : Number(computedPayableFromDays ?? 0);
  const displayedEffectivePayable = Number.isFinite(Number(effectivePayable)) ? Number(effectivePayable) : 0;
  const issueSummary = emp?.issueSummary || {};
  const totalIssues =
    (issueSummary.missingPunches ?? 0) +
    (issueSummary.lateMarks ?? 0) +
    (issueSummary.halfDays ?? 0) +
    (issueSummary.pendingRequests ?? 0);

  if (!employees.length) {
    return (
      <div style={{ maxWidth: 1500, margin: "0 auto", padding: "24px" }}>
        <div style={{ ...s.card, padding: 32, textAlign: "center" }}>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>No employees to review</p>
          <p style={{ color: C.muted, fontSize: 12, margin: 0 }}>
            Go to Attendance Inputs and click <strong>Send to Payroll</strong> to load the payroll preview.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1500, margin: "0 auto", padding: "24px" }}>

      {usingApiPreview && (
        <div
          style={{
            ...s.card,
            padding: "12px 16px",
            marginBottom: 16,
            borderLeft: `3px solid ${C.accent}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <span style={{ fontSize: 12 }}>
            <i className="fa-solid fa-database" style={{ color: C.accent, marginRight: 8 }} />
            Showing <strong>{employees.length}</strong> employees from payroll preview ({monthLabel})
            {detailLoading && <span style={{ marginLeft: 8, color: C.muted }}>· Loading detail…</span>}
          </span>
          <span style={{ fontSize: 11, color: C.muted }}>
            Attendance detail loaded via fetchDetail API
          </span>
        </div>
      )}

      {/* ── DR Toast ── */}
      {drToast && (
        <div style={{ position: 'fixed', bottom: 90, right: 24, zIndex: 99999, background: drToast.type === 'success' ? 'rgba(74,222,128,0.15)' : 'rgba(239,68,68,0.15)', border: `1px solid ${drToast.type === 'success' ? 'rgba(74,222,128,0.4)' : 'rgba(239,68,68,0.4)'}`, borderRadius: 10, padding: '10px 18px', color: drToast.type === 'success' ? '#4ADE80' : '#EF4444', fontSize: 12, fontWeight: 500, backdropFilter: 'blur(8px)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className={`fa-solid ${drToast.type === 'success' ? 'fa-circle-check' : 'fa-circle-xmark'}`} />
          {drToast.msg}
        </div>
      )}

      {/* ── Open Requests Modal ── */}
      {showRequestModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setShowRequestModal(false)}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, maxWidth: 520, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>Open Requests — {emp.name}</div>
              <button onClick={() => setShowRequestModal(false)} style={{ ...s.elevated, width: 30, height: 30, borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}><i className="fa-solid fa-xmark" style={{ fontSize: 11 }} /></button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              {(emp.requests || []).length === 0 ? (
                <p style={{ color: C.muted, fontSize: 12, textAlign: 'center' }}>No open requests</p>
              ) : (emp.requests || []).map((req, i) => (
                <div key={i} style={{ ...s.elevated, padding: 12, borderRadius: 10, marginBottom: 10, borderLeft: `3px solid ${C.warning}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 12 }}>{req.date || req.title} — {req.type || req.issueType}</span>
                    <StatusChip status={req.status} />
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>{req.detail}</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => { drShowToast(`Approved: ${req.type}`); setShowRequestModal(false); }} style={{ ...s.btnSuccess, padding: '4px 10px', fontSize: 10 }}><i className="fa-solid fa-check" />Approve</button>
                    <button onClick={() => { drShowToast(`Rejected: ${req.type}`, 'error'); setShowRequestModal(false); }} style={{ ...s.btnDanger, padding: '4px 10px', fontSize: 10 }}><i className="fa-solid fa-xmark" />Reject</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Resolve Modal ── */}
      {showResolveModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setShowResolveModal(false)}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, maxWidth: 520, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>Resolve Issues — {emp.name}</div>
              <button onClick={() => setShowResolveModal(false)} style={{ ...s.elevated, width: 30, height: 30, borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}><i className="fa-solid fa-xmark" style={{ fontSize: 11 }} /></button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              {((emp.issues || []).length ? emp.issues : [
                issueSummary.missingPunches ? { icon: 'fa-circle-exclamation', color: C.error, label: `Missing Punches (${issueSummary.missingPunches})`, action: 'Resolve', fix: 'From attendance detail' } : null,
                issueSummary.lateMarks ? { icon: 'fa-clock', color: C.warning, label: `Late Marks (${issueSummary.lateMarks})`, action: 'Resolve', fix: 'From attendance detail' } : null,
                issueSummary.halfDays ? { icon: 'fa-calendar-minus', color: '#60A5FA', label: `Half Days (${issueSummary.halfDays})`, action: 'Resolve', fix: 'From attendance detail' } : null,
                issueSummary.pendingRequests ? { icon: 'fa-file-pen', color: '#C084FC', label: `Pending Requests (${issueSummary.pendingRequests})`, action: 'Resolve', fix: 'From attendance detail' } : null,
              ].filter(Boolean)).map((item, i) => (
                <div key={i} style={{ ...s.elevated, padding: 12, borderRadius: 10, marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: `${item.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className={`fa-solid ${item.icon}`} style={{ color: item.color, fontSize: 12 }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 12 }}>{item.label}</div>
                      <div style={{ fontSize: 10, color: C.muted }}>{item.fix}</div>
                    </div>
                  </div>
                  <button onClick={() => { drShowToast(`Resolved: ${item.label}`); setShowResolveModal(false); }} style={{ ...s.btnPrimary, padding: '5px 12px', fontSize: 10, boxShadow: 'none', whiteSpace: 'nowrap' }}>{item.action}</button>
                </div>
              ))}
              <button onClick={() => { handleResolveIssues(); drShowToast('All issues resolved'); setShowResolveModal(false); setCurrentCompleted(true); }} style={{ ...s.btnPrimary, width: '100%', justifyContent: 'center', marginTop: 4 }}>
                <i className="fa-solid fa-wand-magic-sparkles" /> Resolve All Issues
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <h2 style={{ margin: 0, fontSize: 12, fontWeight: 700 }}>Attendance Detail Review</h2>
              <StatusChip status={empStatus} />
              <span style={{ fontSize: 10, color: C.muted }}>({empIndex + 1} / {employees.length})</span>
            </div>
            <div style={{ color: C.muted, fontSize: 12 }}>Verify employee attendance, payable days, punches & exceptions</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            style={{ ...s.btnGhost, color: empStatus === 'Hold' ? C.warning : undefined, borderColor: empStatus === 'Hold' ? 'rgba(245,158,11,0.4)' : undefined }}
            onClick={handleHold}
          >
            <i className="fa-solid fa-pause" style={{ fontSize: 11 }} />
            {empStatus === 'Hold' ? 'On Hold' : 'Hold'}
          </button>
          <button
            style={{ ...s.btnSecondary, opacity: currentCompleted ? 1 : 0.5, cursor: currentCompleted ? 'pointer' : 'not-allowed' }}
            onClick={handleNext}
            title={!currentCompleted ? 'Save or approve current employee first' : 'Next Employee'}
          >
            <i className="fa-solid fa-chevron-right" />Next Employee
          </button>
          <button style={s.btnPrimary} onClick={handleApprove}>
            <i className="fa-solid fa-check" />Approve Attendance
          </button>
        </div>
      </div>
      <div style={{ ...s.card, padding: 20, marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <img src={emp.avatar} style={{ width: 72, height: 72, borderRadius: 12, objectFit: "cover", border: "2px solid rgba(134,96,248,0.3)" }} alt="" />
            <div>
              <div style={{ fontWeight: 700,fontSize: 12, marginBottom: 4 }}>{emp.name}</div>
              <div style={{ display: "flex", gap: 20, color: C.muted, fontSize: 12, flexWrap: "wrap" }}>
                {[["fa-id-badge", emp.empId, C.accent],["fa-briefcase", emp.dept, null],["fa-location-dot", emp.branch || "—", null],["fa-clock", emp.shift || "—", null]].map(([icon,text,color]) => (
                  <span key={text} style={{ display: "flex", alignItems: "center", gap: 5 }}><i className={`fa-solid ${icon}`} style={{ color: color || C.muted }} />{text}</span>
                ))}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            {[["Present", empDayCounts.present ?? 0, C.success],["LOP", empDayCounts.lop ?? 0, C.error],["OT", `${Number(empDayCounts.ot ?? 0)}h`, C.accent],["Payable", displayedEffectivePayable, "#fff"]].map(([l,v,c]) => (
              <div key={l} style={{ ...s.elevated, padding: "10px 18px", textAlign: "center", borderLeft: `2px solid ${c}` }}>
                <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>{l}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: c }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr 260px", gap: 20 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ ...s.card, padding: 16 }}>
            <div style={{ fontWeight: 600,fontSize: 12, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><i className="fa-solid fa-user" style={{ color: C.accent }} />Employee Info</div>
            {[["Department", emp.dept || "—"], ["Employee ID", emp.empId], ["Staff ID", emp.staffId ?? "—"]].map(([k,v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, fontSize: 12 }}>
                <span style={{ color: C.muted }}>{k}</span><span style={{ fontWeight: 500 }}>{v}</span>
              </div>
            ))}
          </div>
          {emp.fromApi ? (
            <div style={{ ...s.card, padding: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <i className="fa-solid fa-indian-rupee-sign" style={{ color: C.accent }} />
                Payroll preview — {monthLabel}
              </div>
              {[
                ["Gross salary", formatInr(emp.grossSalary)],
                ["Net salary", formatInr(emp.netSalary)],
                ["TDS", formatInr(emp.tds)],
                ["Basic", formatInr(emp.components?.basic)],
                ["Incentives", formatInr(emp.components?.incentives)],
                ["Reimbursements", formatInr(emp.components?.reimbursements)],
              ].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 12 }}>
                  <span style={{ color: C.muted }}>{k}</span>
                  <span style={{ fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>
          ) : null}
          <div style={{ ...s.card, padding: 16 }}>
            <div style={{ fontWeight: 600,fontSize: 12, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><i className="fa-solid fa-triangle-exclamation" style={{ color: C.warning }} />Issue Summary</div>
            {[["fa-circle-exclamation",C.error,"Missing Punches",issueSummary.missingPunches ?? 0],["fa-clock",C.warning,"Late Marks",issueSummary.lateMarks ?? 0],["fa-calendar-minus","#60A5FA","Half Days",issueSummary.halfDays ?? 0],["fa-file-pen","#C084FC","Pending Requests",issueSummary.pendingRequests ?? 0]].map(([icon,color,label,count]) => (
              <div key={label} style={{ ...s.elevated, padding: 10, borderRadius: 8, marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 6, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}><i className={`fa-solid ${icon}`} style={{ color,fontSize: 12 }} /></div>
                  <span style={{ fontSize: 12, fontWeight: 500 }}>{label}</span>
                </div>
                <span style={{ fontWeight: 700, color,fontSize: 12 }}>{count}</span>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={() => setShowRequestModal(true)} style={{ ...s.btnGhost, flex: 1, justifyContent: "center",fontSize: 12 }}>Open Requests</button>
              <button onClick={() => setShowResolveModal(true)} style={{ ...s.btnSuccess, flex: 1, justifyContent: "center",fontSize: 12 }}>Resolve</button>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ ...s.card, padding: "10px 12px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 4 }}>
              <div style={{ fontWeight: 600, fontSize: 10 }}>Monthly Calendar — {monthLabel}{detailLoading ? " (loading…)" : ""}</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[["present","Present"],["absent","Absent"],["lop","LOP"],["missing","Missing"]].map(([type,label]) => {
                  const dc = dayColors[type];
                  return <span key={label} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 8, color: C.muted }}><span style={{ width: 6, height: 6, borderRadius: 1, background: dc.bg, border: `1px ${dc.dashed?"dashed":"solid"} ${dc.border}`, flexShrink: 0 }} />{label}</span>;
                })}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 2 }}>
              {["M","T","W","T","F","S","S"].map((d, i) => <div key={i} style={{ textAlign: "center", fontSize: 7, fontWeight: 600, color: C.muted }}>{d}</div>)}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
              {activeDayData.length === 0 ? (
                <div style={{ gridColumn: "1 / -1", padding: 16, textAlign: "center", color: C.muted, fontSize: 11 }}>No calendar data from API</div>
              ) : activeDayData.map(({ d, type }) => {
                const dc = dayColors[type] || dayColors.present;
                const isSel = d === selectedDay;
                return (
                  <div key={d} onClick={() => setSelectedDay(d)} style={{ height: 18, borderRadius: 3, background: dc.bg, cursor: "pointer", border: `${dc.dashed ? "1.5px dashed" : "1px solid"} ${isSel ? C.accent : dc.border}`, boxShadow: isSel ? `0 0 0 1.5px rgba(134,96,248,0.4)` : "none", transition: "all 0.1s", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: 7, fontWeight: isSel ? 700 : 500, color: isSel ? C.accent : dc.color, lineHeight: 1 }}>{d}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ ...s.card, padding: 20 }}>
            <div style={{ fontWeight: 600,fontSize: 12, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}><i className="fa-solid fa-calendar-day" style={{ color: C.accent }} />Selected Day Details</div>
            <div style={{ ...s.elevated, padding: 16, borderRadius: 10, marginBottom: 14, borderLeft: `4px solid ${selDayColor.color}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 12 }}>
                  {selectedDay} {monthLabel} — {getDayName(selectedDay)}
                </span>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {/* Type badge */}
                  <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 20, background: `${selDayColor.color}18`, color: selDayColor.color, border: `1px solid ${selDayColor.border}`, fontWeight: 600, textTransform: 'capitalize' }}>
                    {selDayData.type.charAt(0).toUpperCase() + selDayData.type.slice(1)}
                  </span>
                  {/* Extra badge if any */}
                  {activeDayDetails[selectedDay]?.badge && activeDayDetails[selectedDay].badge !== selDayData.type.charAt(0).toUpperCase() + selDayData.type.slice(1) && (
                    <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 20, background: "rgba(245,158,11,0.1)", color: C.warning, border: "1px solid rgba(245,158,11,0.3)", fontWeight: 600 }}>
                      {activeDayDetails[selectedDay].badge}
                    </span>
                  )}
                </div>
              </div>
              {/* Punch details grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, fontSize: 12 }}>
                {[
                  ["Clock In",      activeDayDetails[selectedDay]?.in      || "—", C.success],
                  ["Clock Out",     activeDayDetails[selectedDay]?.out     || "—", C.warning],
                  ["Worked Hours",  activeDayDetails[selectedDay]?.worked  || "—", "#fff"],
                  ["Note",          activeDayDetails[selectedDay]?.note    || "—", C.muted],
                ].map(([l, v, c]) => (
                  <div key={l}>
                    <div style={{ color: C.muted, marginBottom: 3, fontSize: 10 }}>{l}</div>
                    <div style={{ fontWeight: 600, color: c, fontSize: 11 }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={() => { handleUpdateDay(selectedDay, "present"); drShowToast(`Correction approved for day ${selectedDay}`); setCurrentCompleted(true); }} style={s.btnSuccess}><i className="fa-solid fa-check" />Approve Correction</button>
              <button onClick={() => { handleUpdateDay(selectedDay, "present"); drShowToast(`Day ${selectedDay} marked as Present`); }} style={s.btnSecondary}><i className="fa-solid fa-circle-check" />Mark Present</button>
              <button onClick={() => { handleUpdateDay(selectedDay, "half"); drShowToast(`Day ${selectedDay} marked as Half Day`); }} style={s.btnGhost}><i className="fa-solid fa-calendar-minus" />Half Day</button>
              <button onClick={() => { handleUpdateDay(selectedDay, "lop"); drShowToast(`Day ${selectedDay} marked as LOP`, 'error'); }} style={s.btnDanger}><i className="fa-solid fa-ban" />Mark LOP</button>
            </div>
          </div>
          <div style={{ ...s.card, padding: 20 }}>
            <div style={{ fontWeight: 600,fontSize: 12, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}><i className="fa-solid fa-calculator" style={{ color: C.accent }} />Payable Days Formula</div>
            {[
              ["Present", String(empDayCounts.present ?? 0), C.success],
              ["LOP / Absent", String(empDayCounts.lop ?? 0), C.error],
              ["OT Hours", `${empDayCounts.ot ?? 0}h`, C.warning],
              ["Half days", String(empDayCounts.half ?? 0), C.muted],
              ["Paid leave", String(empDayCounts.paidLeave ?? 0), C.muted],
              ["Holidays", String(empDayCounts.holidays ?? 0), C.muted],
            ].map(([l,v,c]) => (
              <div key={l} style={{ ...s.elevated, padding: "10px 14px", marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center", borderLeft: l.includes("LOP") ? `3px solid ${C.error}` : "none" }}>
                <span style={{fontSize: 12, color: C.muted }}>{l}</span><span style={{ fontWeight: 700, color: c }}>{v}</span>
              </div>
            ))}
            <div style={{ ...s.elevated, padding: "14px", marginTop: 4, display: "flex", justifyContent: "space-between", alignItems: "center", borderLeft: `4px solid ${C.accent}`, boxShadow: `0 0 16px rgba(134,96,248,0.25)` }}>
              <span style={{ fontWeight: 600 }}>Monthly Payable Days</span>
              <span style={{ fontWeight: 700, fontSize: 22, color: C.accent }}>{displayedEffectivePayable}</span>
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: C.muted }}>
              Calculated formula: {computedPayableFromDays} days for the full month, not just the single day change.
            </div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ ...s.card, padding: 16 }}>
            <div style={{ fontWeight: 600,fontSize: 12, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><i className="fa-solid fa-file-pen" style={{ color: C.accent }} />Regularization Requests</div>
            {(emp.requests || []).length === 0 ? (
              <div style={{ ...s.elevated, padding: 12, color: C.muted, fontSize: 12, textAlign: "center" }}>No pending regularization requests</div>
            ) : (emp.requests || []).map((req, i) => (
              <div key={req.id || i} style={{ ...s.elevated, padding: 12, borderLeft: `2px solid ${C.warning}`, borderRadius: 8, marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 12 }}>
                  <span style={{ fontWeight: 600 }}>{req.date || req.title || "Request"} — {req.type || req.issueType || "Regularization"}</span>
                  <StatusChip status={req.status || "Pending"} />
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>{req.detail || req.description || ""}</div>
                {req.remarks && <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>{req.remarks}</div>}
              </div>
            ))}
          </div>
          <div style={{ ...s.card, padding: 16 }}>
            {/* Header with monthly badge */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <div style={{ fontWeight: 600, fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <i className="fa-solid fa-pen-to-square" style={{ color: C.accent }} />
                Manual Adjustments
              </div>
              <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: "rgba(134,96,248,0.15)", color: C.accent, border: "1px solid rgba(134,96,248,0.3)", letterSpacing: "0.04em" }}>
                MONTHLY
              </span>
            </div>
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 14, padding: "6px 8px", background: "rgba(134,96,248,0.06)", borderRadius: 6, borderLeft: `2px solid rgba(134,96,248,0.3)` }}>
              <i className="fa-solid fa-circle-info" style={{ color: C.accent, marginRight: 5, fontSize: 9 }} />
              These adjustments apply to <strong style={{ color: "var(--text-primary)" }}>{emp.name}</strong> for {monthLabel}.
            </div>

            {[["OT Hours", otAdj, setOtAdj, "fa-clock", C.accent, "Total overtime hours for the month"],
              ["LOP Days", lopAdj, setLopAdj, "fa-calendar-minus", C.error, "Loss of pay days for the month"],
              ["Payable Override", payAdj, setPayAdj, "fa-calendar-check", C.success, "Override calculated payable days"]
            ].map(([label, val, setter, icon, color, hint]) => (
              <div key={label} style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, color: C.muted, display: "flex", alignItems: "center", gap: 5, marginBottom: 5 }}>
                  <i className={`fa-solid ${icon}`} style={{ color, fontSize: 10 }} />
                  {label}
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="number"
                    value={val}
                    onChange={e => setter(e.target.value)}
                    style={{ ...s.input, flex: 1, fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}
                  />
                  <span style={{ fontSize: 9, color: C.muted, minWidth: 60, lineHeight: 1.3 }}>{hint}</span>
                </div>
              </div>
            ))}

            {/* Summary preview */}
            <div style={{ ...s.elevated, padding: "10px 12px", borderRadius: 8, marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: C.muted }}>Effective Payable Days</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: C.accent }}>
                {effectivePayable}
              </span>
            </div>

            <button
              onClick={() => {
                handleSaveChanges();
                drShowToast(`Monthly adjustments saved for ${emp.name} — ${monthLabel}`);
              }}
              style={{ ...s.btnPrimary, width: "100%", justifyContent: "center", marginTop: 4 }}
            >
              <i className="fa-solid fa-floppy-disk" />Apply Monthly Adjustments
            </button>
          </div>
          <div style={{ ...s.card, padding: 16 }}>
            <div style={{ fontWeight: 600,fontSize: 12, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><i className="fa-solid fa-clock-rotate-left" style={{ color: C.muted }} />Audit Trail</div>
            {(emp.auditTrail || []).length === 0 ? (
              <div style={{ ...s.elevated, padding: 10, color: C.muted, fontSize: 12, textAlign: "center" }}>No audit history</div>
            ) : (emp.auditTrail || []).map((entry, i) => (
              <div key={entry.id || i} style={{ ...s.elevated, padding: 10, borderRadius: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 2 }}>{entry.action || entry.title || entry.message}</div>
                <div style={{ fontSize: 12, color: C.muted }}>{entry.by || entry.user || " "}   {entry.ago || entry.timestamp || ""}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(15,11,36,0.97)", backdropFilter: "blur(12px)", borderTop: `1px solid ${C.border}`, padding: "14px 24px", zIndex: 50 }}>
        <div style={{ maxWidth: 1500, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(134,96,248,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}><i className="fa-solid fa-calendar-check" style={{ color: C.accent,fontSize: 12 }} /></div>
              <div>
                <div style={{ fontWeight: 700,fontSize: 12 }}>{displayedEffectivePayable} Payable Days</div>
                <div style={{fontSize: 12, color: C.muted }}>{empStatus} · {emp.name} ({emp.empId})</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 16,fontSize: 12 }}>
              <span style={{ color: C.error }}><i className="fa-solid fa-circle-exclamation" /> {totalIssues} Issues</span>
              <span style={{ color: C.warning }}><i className="fa-solid fa-file-pen" /> {issueSummary.pendingRequests ?? 0} Requests</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={s.btnSecondary} onClick={handleSaveChanges}><i className="fa-solid fa-floppy-disk" />Save Changes</button>
            <button style={s.btnPrimary}   onClick={handleApprove}><i className="fa-solid fa-check-double" />Approve Attendance</button>
            <button
              style={{ ...s.btnSecondary, opacity: currentCompleted ? 1 : 0.5, cursor: currentCompleted ? 'pointer' : 'not-allowed' }}
              onClick={handleNext}
              title={!currentCompleted ? 'Save or approve current employee first' : 'Next Employee'}
            >
              <i className="fa-solid fa-chevron-right" />Next Employee
            </button>
          </div>
        </div>
      </div>
      <div style={{ height: 70 }} />
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PAGE 3 — APPROVAL QUEUE
// ════════════════════════════════════════════════════════════════════════════
const ApprovalQueue = ({ onEditQueueItem, onBackToInputs }) => {
  const dispatch       = useDispatch();
  const commonStore    = useSelector(s => s.common);
  const user           = JSON.parse(localStorage.getItem("user_data")) || {};
  const branchList     = useSelector(s => s.branch?.brnGetData || []);
  const departmentList = useSelector(s => s.department?.depGetData || []);
  const shiftList      = useSelector(s => s.shift?.shiftGetData || []);

  const [queueRows, setQueueRows] = useState([]);
  const [queueSummary, setQueueSummary] = useState(null);
  const [queueLoading, setQueueLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [toast, setToast] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState("approve");
  const [bulkComments, setBulkComments] = useState("");
  const [queuePage, setQueuePage] = useState(0);
  const [queuePageSize, setQueuePageSize] = useState(10);
  const queueActionRef = useRef(null);

  const todayAQ = new Date();
  const [approvalMonth, setApprovalMonth] = useState(
    `${todayAQ.getFullYear()}-${String(todayAQ.getMonth() + 1).padStart(2, "0")}`
  );
  const [queueBranch, setQueueBranch] = useState("");
  const [queueDept, setQueueDept] = useState("");
  const [queueShift, setQueueShift] = useState("");
  const [queueIssueType, setQueueIssueType] = useState("all");
  const [queueSearch, setQueueSearch] = useState("");

  const fetchQueue = useCallback(() => {
    setQueueLoading(true);
    dispatch(commonSaveDataOne({
      path: payrollProcessPath,
      data: {
        action:       "fetchQueue",
        userId:       user.user_id,
        month:        approvalMonth,
        branchId:     queueBranch || "",
        departmentId: queueDept || "",
        shiftId:      queueShift || "",
        issueType:    queueIssueType || "all",
        search:       queueSearch || "",
      },
    }));
  }, [dispatch, user.user_id, approvalMonth, queueBranch, queueDept, queueShift, queueIssueType, queueSearch]);

  useEffect(() => {
    fetchQueue();
  }, [approvalMonth, queueBranch, queueDept, queueShift, queueIssueType]); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(() => fetchQueue(), 400);
    return () => clearTimeout(t);
  }, [queueSearch]); // eslint-disable-line

  // ── fetchQueue response ──────────────────────────────────────────────────
  useEffect(() => {
    if (commonStore.commonSuccessOne) {
      const raw = commonStore.commonDataOne;
      const rows = Array.isArray(raw)        ? raw
                 : Array.isArray(raw?.queue)  ? raw.queue
                 : Array.isArray(raw?.items)  ? raw.items
                 : Array.isArray(raw?.list)   ? raw.list
                 : [];
      setQueueRows(rows.map(mapQueueItem));
      if (raw?.summary)   setQueueSummary(raw.summary);
      if (raw?.analytics) setQueueSummary(raw.analytics);
      setQueueLoading(false);
      setSelected([]);
      dispatch(initCommonState());
    }
    if (commonStore.commonErrorOne) {
      setQueueLoading(false);
      dispatch(initCommonState());
    }
  }, [commonStore.commonSuccessOne, commonStore.commonErrorOne, commonStore.commonDataOne, dispatch]); // eslint-disable-line

  // ── bulkQueueAction response ─────────────────────────────────────────────
  useEffect(() => {
    if (queueActionRef.current === "bulkQueueAction" && commonStore.commonSuccess) {
      showToast(commonStore.commonMessage || "Bulk action completed");
      queueActionRef.current = null;
      setSelected([]);
      setShowBulkModal(false);
      setBulkComments("");
      dispatch(initCommonState());
      fetchQueue();
    }
    if (queueActionRef.current === "bulkQueueAction" && commonStore.commonError) {
      showToast(commonStore.commonMessage || "Bulk action failed", "error");
      queueActionRef.current = null;
      dispatch(initCommonState());
    }
  }, [commonStore.commonSuccess, commonStore.commonError, dispatch, fetchQueue]); // eslint-disable-line

  const pagedRows = queueRows.slice(queuePage * queuePageSize, (queuePage + 1) * queuePageSize);
  const pendingCount = queueRows.filter((r) => (r.status || "").toLowerCase() === "pending").length;

  const toggleRow = (id) =>
    setSelected((p) => (p.includes(id) ? p.filter((i) => i !== id) : [...p, id]));
  const toggleAll = () => {
    if (selected.length === pagedRows.length) setSelected([]);
    else setSelected(pagedRows.map((r) => r.id ?? r.empId));
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleRowAction = (row, action) => {
    if (action === "edit") {
      onEditQueueItem?.(row, approvalMonth);
      return;
    }
    const itemIds = [row.id].filter(Boolean);
    if (!itemIds.length) {
      showToast("Unable to identify the selected attendance case", "error");
      return;
    }
    const bulkAction = action === "approve" ? "approve" : action === "hold" ? "hold" : action;
    queueActionRef.current = "bulkQueueAction";
    setQueueRows((prev) => prev.map((r) => r.id === row.id ? { ...r, status: bulkAction === "approve" ? "Approved" : bulkAction === "hold" ? "Hold" : r.status } : r));
    dispatch(commonSaveData({
      path: payrollProcessPath,
      data: {
        action:     "bulkQueueAction",
        userId:     user.user_id,
        itemIds,
        bulkAction,
        comments:   `${bulkAction === "approve" ? "Approved" : bulkAction === "hold" ? "Placed on Hold" : bulkAction} via queue`,
      },
    }));
    showToast(`${row.name} ${bulkAction === "approve" ? "approved" : bulkAction === "hold" ? "placed on hold" : bulkAction}`);
  };

  const handleBulkApprove = () => {
    if (selected.length === 0) {
      showToast("Please select at least one item to approve", "error");
      return;
    }
    setBulkAction("approve");
    setShowBulkModal(true);
  };

  const runBulkAction = (action) => {
    const itemIds = selected
      .map((id) => {
        const row = queueRows.find((r) => String(r.id ?? r.empId) === String(id));
        return row?.id;
      })
      .filter(Boolean);
    if (!itemIds.length) {
      showToast("Please select items first", "error");
      return;
    }
    queueActionRef.current = "bulkQueueAction";
    dispatch(commonSaveData({
      path: payrollProcessPath,
      data: {
        action:     "bulkQueueAction",
        userId:     user.user_id,
        itemIds,
        bulkAction: action,
        comments:   bulkComments || `Bulk ${action} by HR`,
      },
    }));
    setShowBulkModal(false);
    setSelected([]);
    showToast(`Bulk ${action} submitted for ${itemIds.length} item${itemIds.length > 1 ? "s" : ""}`);
  };

  const confirmBulkApprove = () => runBulkAction(bulkAction);

  return (
    <div style={{ maxWidth: 1500, margin: "0 auto", padding: "24px 24px 120px" }}>

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: toast.type === 'error' ? 'rgba(239,68,68,0.95)' : toast.type === 'warning' ? 'rgba(245,158,11,0.95)' : 'rgba(74,222,128,0.95)',
          color: '#fff', padding: '12px 20px', borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          fontSize: 13, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <i className={`fa-solid fa-${toast.type === 'error' ? 'circle-xmark' : toast.type === 'warning' ? 'circle-exclamation' : 'circle-check'}`} />
          {toast.msg}
        </div>
      )}

      {/* ── Bulk Action Modal (triggered by select all or bulk approve) ── */}
      {showBulkModal && (
        <div onClick={() => setShowBulkModal(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
          backdropFilter: 'blur(6px)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 16, width: 460, boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding: '18px 20px', borderBottom: '1px solid var(--border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: 'rgba(134,96,248,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <i className="fa-solid fa-list-check" style={{ color: C.accent, fontSize: 14 }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Bulk Action</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{selected.length} {selected.length === 1 ? 'case' : 'cases'} selected</div>
                </div>
              </div>
              <button onClick={() => setShowBulkModal(false)} style={{
                width: 30, height: 30, borderRadius: '50%', border: 'none',
                background: 'var(--pay-surface-2)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted,
              }}>
                <i className="fa-solid fa-xmark" style={{ fontSize: 12 }} />
              </button>
            </div>

            {/* Selected employees preview */}
            <div style={{ padding: '16px 20px 0' }}>
              <div style={{
                background: 'var(--pay-surface-2)', borderRadius: 10,
                padding: '10px 14px', maxHeight: 180, overflowY: 'auto',
              }}>
                {selected.map((i, idx) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '6px 0',
                    borderBottom: idx !== selected.length - 1 ? '1px solid rgba(169,163,201,0.08)' : 'none',
                  }}>
                    <img src={(queueRows.find((r) => (r.id ?? r.empId) === i) || {}).avatar} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} alt="" />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{(queueRows.find((r) => (r.id ?? r.empId) === i) || {}).name}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{(queueRows.find((r) => (r.id ?? r.empId) === i) || {}).issue}</div>
                    </div>
                    <StatusChip status={(queueRows.find((r) => (r.id ?? r.empId) === i) || {}).priority} />
                  </div>
                ))}
              </div>

              <div style={{
                background: 'rgba(134,96,248,0.08)', border: '1px solid rgba(134,96,248,0.2)',
                borderRadius: 8, padding: '10px 14px', margin: '14px 0',
                display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.muted,
              }}>
                <i className="fa-solid fa-circle-info" style={{ color: C.accent }} />
                Choose an action to apply to all selected cases.
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ padding: '0 20px 20px', display: 'flex', gap: 10 }}>
              <button onClick={() => runBulkAction("hold")} style={{
                flex: 1, padding: '10px 0', borderRadius: 10, border: '1px solid rgba(245,158,11,0.3)',
                background: 'rgba(245,158,11,0.1)', color: C.warning,
                cursor: 'pointer', fontWeight: 600, fontSize: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                <i className="fa-solid fa-pause" />Hold All
              </button>
              <button onClick={() => runBulkAction("reject")} style={{
                flex: 1, padding: '10px 0', borderRadius: 10, border: '1px solid rgba(239,68,68,0.3)',
                background: 'rgba(239,68,68,0.1)', color: C.error,
                cursor: 'pointer', fontWeight: 600, fontSize: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                <i className="fa-solid fa-xmark" />Reject All
              </button>
              <button onClick={confirmBulkApprove} style={{
                flex: 1, padding: '10px 0', borderRadius: 10, border: 'none',
                background: `linear-gradient(135deg, ${C.accent}, #6b4cd4)`,
                color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                boxShadow: '0 4px 15px rgba(134,96,248,0.4)',
              }}>
                <i className="fa-solid fa-check-double" />Approve All
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 14 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
            <h2 style={{ margin: 0,fontSize: 12, fontWeight: 700 }}>Attendance Approval Queue</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 20, background: "rgba(239,68,68,0.1)", border: `1px solid rgba(239,68,68,0.3)` }}>
              <span style={{ width: 8, height: 8, background: C.error, borderRadius: "50%", animation: "pulse 2s infinite" }} />
              <span style={{ color: C.error, fontWeight: 600,fontSize: 12 }}>{pendingCount} Pending</span>
            </div>
          </div>
          <p style={{ margin: 0, color: C.muted, fontSize: 12 }}>Review and approve all attendance cases before running payroll · April 2026</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button style={s.btnGhost}><i className="fa-solid fa-file-export" />Export Queue</button>
          <button style={s.btnGhost} onClick={fetchQueue} disabled={queueLoading}><i className="fa-solid fa-rotate" />{queueLoading ? "Loading…" : "Refresh"}</button>
          <button style={{ ...s.btnSecondary }}><i className="fa-solid fa-wand-magic-sparkles" />Run Auto Solve</button>
          <button style={{ ...s.btnPrimary, boxShadow: "0 0 18px rgba(134,96,248,0.35)" }} onClick={handleBulkApprove}>
            <i className="fa-solid fa-check-double" />
            Bulk Approve{selected.length > 0 ? ` (${selected.length})` : ''}
          </button>
        </div>
      </div>
      <div style={{ ...s.card, padding: 12, marginBottom: 16 }}>
        {/* Filter row */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 12 }}>
          {/* Month picker */}
          <MonthPicker
            value={approvalMonth}
            onChange={(val) => setApprovalMonth(val)}
          />
          {/* Branch */}
          <div className="field-wrap has-right-icon" style={{ flex: "0 0 130px", minWidth: 0 }}>
            <select className="filter-select" value={queueBranch} onChange={e => setQueueBranch(e.target.value)} style={{ width: "100%", fontSize: 11, padding: "6px 28px 6px 10px" }}>
              <option value="">All Branches</option>
              {branchList.map(b => <option key={b.branchId} value={b.branchId}>{b.branch_name}</option>)}
            </select>
            <span className="field-icon-right" />
          </div>
          {/* Department */}
          <div className="field-wrap has-right-icon" style={{ flex: "0 0 145px", minWidth: 0 }}>
            <select className="filter-select" value={queueDept} onChange={e => setQueueDept(e.target.value)} style={{ width: "100%", fontSize: 11, padding: "6px 28px 6px 10px" }}>
              <option value="">All Departments</option>
              {departmentList.map(d => <option key={d.departmentId} value={d.departmentId}>{d.department_name}</option>)}
            </select>
            <span className="field-icon-right" />
          </div>
          {/* Shift */}
          <div className="field-wrap has-right-icon" style={{ flex: "0 0 120px", minWidth: 0 }}>
            <select className="filter-select" value={queueShift} onChange={e => setQueueShift(e.target.value)} style={{ width: "100%", fontSize: 11, padding: "6px 28px 6px 10px" }}>
              <option value="">All Shifts</option>
              {shiftList.map(sh => <option key={sh.shiftId} value={sh.shiftId}>{sh.shiftName}</option>)}
            </select>
            <span className="field-icon-right" />
          </div>
          {/* Issue Type */}
          <div className="field-wrap has-right-icon" style={{ flex: "0 0 120px", minWidth: 0 }}>
            <select className="filter-select" value={queueIssueType} onChange={e => setQueueIssueType(e.target.value)} style={{ width: "100%", fontSize: 11, padding: "6px 28px 6px 10px" }}>
              <option value="all">All Issues</option>
              <option value="missing_punch">Missing Punch</option>
              <option value="late">Late Mark</option>
              <option value="geo">Geo Fence</option>
              <option value="regularization">Regularization</option>
            </select>
            <span className="field-icon-right" />
          </div>
          {/* Priority */}
          <div className="field-wrap has-right-icon" style={{ flex: "0 0 115px", minWidth: 0 }}>
            <select className="filter-select" style={{ width: "100%", fontSize: 11, padding: "6px 28px 6px 10px" }}>
              <option>All Priority</option>
            </select>
            <span className="field-icon-right" />
          </div>
        </div>
        {/* Search + toggles row */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div className="field-wrap field-search" style={{ flex: "0 0 220px", minWidth: 0 }}>
            <span className="field-icon" />
            <input placeholder="Search employee, ID, issue..." value={queueSearch} onChange={e => setQueueSearch(e.target.value)} style={{ width: "100%", fontSize: 11, padding: "6px 12px 6px 30px", cursor: "text", boxSizing: "border-box", background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: 8, color: "var(--input-color)", fontFamily: "var(--font)", outline: "none" }} />
          </div>
          <div style={{ display: "flex", gap: 16 }}>
            {["Only Urgent","Only Today"].map(l => (
              <label key={l} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 11, color: C.muted }}>
                <input type="checkbox" style={{ accentColor: C.accent }} />{l}
              </label>
            ))}
          </div>
        </div>
      </div>
      <div style={{ ...s.card, marginBottom: 16 }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: C.elevated }}>
                <th style={{ padding: "12px 16px", width: 44 }}><input type="checkbox" onChange={toggleAll} checked={pagedRows.length > 0 && selected.length === pagedRows.length} style={{ accentColor: C.accent }} /></th>
                {["Employee","Issue Details","Date","Source","Priority","Status","Requested By","Actions"].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: h === "Actions" ? "right" : "left",fontSize: 12, fontWeight: 600, color: C.muted, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((row) => {
                const rowKey = row.id ?? row.empId;
                return (
                <tr key={rowKey} style={{ borderBottom: `1px solid rgba(169,163,201,0.05)`, borderLeft: `3px solid ${row.priority === "Urgent" ? C.error : row.priority === "High" ? C.error : row.priority === "Medium" ? C.warning : C.success}` }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(134,96,248,0.03)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "14px 16px" }}><input type="checkbox" checked={selected.includes(rowKey)} onChange={() => toggleRow(rowKey)} style={{ accentColor: C.accent }} /></td>
                  <td style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <img src={row.avatar} style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }} alt="" />
                      <div><div style={{ fontWeight: 600,fontSize: 12 }}>{row.name}</div><div style={{fontSize: 12, color: C.muted }}>{row.empId} · {row.dept}</div></div>
                    </div>
                  </td>
                  <td style={{ padding: "14px 16px" }}><div style={{ fontWeight: 500,fontSize: 12 }}>{row.issue}</div><div style={{fontSize: 12, color: C.muted }}>{row.detail}</div></td>
                  <td style={{ padding: "14px 16px" }}><div style={{ fontWeight: 500,fontSize: 12 }}>{row.date}</div><div style={{fontSize: 12, color: C.muted }}>{row.day}</div></td>
                  <td style={{ padding: "14px 16px" }}><span style={{fontSize: 12, padding: "4px 10px", borderRadius: 20, background: `${row.srcColor}18`, color: row.srcColor, border: `1px solid ${row.srcColor}40` }}>{row.source}</span></td>
                  <td style={{ padding: "14px 16px" }}><StatusChip status={row.priority} /></td>
                  <td style={{ padding: "14px 16px" }}><StatusChip status={row.status} /></td>
                  <td style={{ padding: "14px 16px" }}><div style={{ fontSize: 12, fontWeight: 500 }}>{row.by}</div><div style={{fontSize: 12, color: C.muted }}>{row.ago}</div></td>
                  <td style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      {[["check",C.success,"Approve"],["xmark",C.error,"Reject"],["clock",C.warning,"Hold"]].map(([icon,color,title]) => (
                        <button key={icon} title={title} style={{ width: 30, height: 30, borderRadius: "50%", ...s.elevated, border: "none", color, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <i className={`fa-solid fa-${icon}`} style={{fontSize: 12 }} />
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", borderTop: `1px solid ${C.border}` }}>
          <span style={{ fontSize: 12, color: C.muted }}>
            {queueRows.length === 0
              ? (queueLoading ? "Loading queue…" : "No cases in queue")
              : `Showing ${queuePage * queuePageSize + 1}–${Math.min((queuePage + 1) * queuePageSize, queueRows.length)} of ${queueRows.length} cases`}
          </span>
          <CustomPagination
            total={queueRows.length}
            page={queuePage}
            pageSize={queuePageSize}
            onPageChange={setQueuePage}
            onPageSizeChange={(s) => { setQueuePageSize(s); setQueuePage(0); }}
          />
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
        <div style={{ ...s.card, padding: 20 }}>
          <div style={{ fontWeight: 600,fontSize: 12, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}><i className="fa-solid fa-chart-pie" style={{ color: C.accent }} />Approval Analytics</div>
          {[
            ["Approval Rate", queueSummary?.approvalRate ?? (queueRows.length ? `${Math.round(((queueRows.length - pendingCount) / queueRows.length) * 100)}%` : "—"), C.success, queueSummary?.approvalRatePct],
            ["Avg. Resolution Time", queueSummary?.avgResolutionTime ?? "—", C.accent, null],
            ["Auto Resolved", queueSummary?.autoResolved ?? "—", "#60A5FA", null],
          ].map(([l,v,c,pct]) => (
            <div key={l} style={{ ...s.elevated, padding: 12, marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: pct ? 8 : 0 }}>
                <span style={{ fontSize: 12, color: C.muted }}>{l}</span><span style={{ fontWeight: 700, color: c,fontSize: 12 }}>{v}</span>
              </div>
              {pct && <div style={{ height: 6, background: C.elevated, borderRadius: 4, overflow: "hidden" }}><div style={{ height: "100%", background: c, width: `${pct}%`, borderRadius: 4 }} /></div>}
            </div>
          ))}
        </div>
        <div style={{ ...s.card, padding: 20 }}>
          <div style={{ fontWeight: 600,fontSize: 12, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}><i className="fa-solid fa-robot" style={{ color: "#60A5FA" }} />Auto Fix Suggestions</div>
          <div style={{ ...s.elevated, padding: 12, borderLeft: `2px solid #60A5FA`, marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span style={{fontSize: 12, fontWeight: 500 }}>Safe to Auto Approve</span><span style={{ fontWeight: 700, color: C.accent }}>{queueSummary?.safeToApprove ?? "—"}</span></div>
            <div style={{fontSize: 12, color: C.muted }}>Low risk cases with manager approval</div>
          </div>
          <div style={{ ...s.elevated, padding: 12, borderLeft: `2px solid ${C.warning}`, marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span style={{fontSize: 12, fontWeight: 500 }}>Missing Punch Auto Fix</span><span style={{ fontWeight: 700, color: C.accent }}>{queueSummary?.missingPunchAutoFix ?? "—"}</span></div>
            <div style={{fontSize: 12, color: C.muted }}>Can be resolved using shift timings</div>
          </div>
          <button style={{ ...s.btnPrimary, width: "100%", justifyContent: "center" }}><i className="fa-solid fa-wand-magic-sparkles" />Run Auto Resolve</button>
        </div>
        <div style={{ ...s.card, padding: 20 }}>
          <div style={{ fontWeight: 600,fontSize: 12, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}><i className="fa-solid fa-clock-rotate-left" style={{ color: C.muted }} />Recent Actions</div>
          {(queueSummary?.recentActions || []).length === 0 ? (
            <div style={{ ...s.elevated, padding: 10, color: C.muted, fontSize: 12, textAlign: "center" }}>No recent actions</div>
          ) : (queueSummary.recentActions).map((item, i) => (
            <div key={item.id || i} style={{ ...s.elevated, padding: 10, borderRadius: 8, marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 2 }}>{item.action || item.title}</div>
              <div style={{fontSize: 12, color: C.muted }}>{item.by || item.user} · {item.ago || item.timestamp}</div>
            </div>
          ))}
        </div>
      </div>
      {selected.length > 0 && null}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(15,11,36,0.97)", backdropFilter: "blur(12px)", borderTop: `1px solid ${C.border}`, padding: "14px 24px", zIndex: 40 }}>
        <div style={{ maxWidth: 1500, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(245,158,11,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}><i className="fa-solid fa-clock" style={{ color: C.warning,fontSize: 12 }} /></div>
              <div>
                <div style={{ fontWeight: 700,fontSize: 12 }}>{`${pendingCount} Cases Pending Review`}</div>
                <div style={{fontSize: 12, color: C.muted }}>Clear queue before payroll run · {formatPreviewMonthLabel(approvalMonth)}</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 16,fontSize: 12 }}>
              <span style={{ color: C.error }}><i className="fa-solid fa-circle-exclamation" /> {queueRows.filter((r) => r.priority === "Urgent").length} Urgent</span>
              <span style={{ color: "#60A5FA" }}><i className="fa-solid fa-robot" /> {queueSummary?.autoFixable ?? queueSummary?.safeToApprove ?? "—"} Auto Fix</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={s.btnSecondary}><i className="fa-solid fa-wand-magic-sparkles" />Approve All Safe Cases</button>
            <button style={s.btnGhost} onClick={onBackToInputs}><i className="fa-solid fa-arrow-left" />Back to Inputs</button>
            <button style={{ ...s.btnSecondary, opacity: 0.5, cursor: "not-allowed" }}><i className="fa-solid fa-lock" />Proceed to Run Payroll</button>
          </div>
        </div>
      </div>
      <style>{`@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }`}</style>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// ROOT
// ════════════════════════════════════════════════════════════════════════════
export default function App() {
  const [activeTab, setActiveTab] = useState("inputs");
  const [payrollPreview, setPayrollPreview] = useState(null);

  const handleEditQueueItem = (row, month) => {
    setPayrollPreview({ selectedMonth: month, employees: [row] });
    setActiveTab("detail");
  };

  const pages = {
    inputs: (
      <AttendanceInputs
        setActiveTab={setActiveTab}
        onPayrollPreviewReady={setPayrollPreview}
      />
    ),
    detail: <DetailReview payrollPreview={payrollPreview} />,
    queue:  <ApprovalQueue onEditQueueItem={handleEditQueueItem} onBackToInputs={() => setActiveTab("inputs")} />,
  };

  return (
    <div style={{ background: "linear-gradient(135deg, #0d0b1e 0%, #1a1040 50%, #0f0d2a 100%)", color: "var(--pay-text)", height: "100vh", display: "flex", overflow: "hidden" }}>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />

      {/* ── Hover-expand side nav — no isOpen prop needed ── */}
      <Nav activeTab={activeTab} setActiveTab={setActiveTab} />

      {/* ── Main content ── */}
      <div style={{ flex: 1, minWidth: 0, overflow: "auto" }}>
        {pages[activeTab]}
      </div>

      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: var(--pay-surface); }
        ::-webkit-scrollbar-thumb { background: var(--pay-surface-2); border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: #8660F8; }
        select option { background: var(--pay-surface-2); color: var(--pay-text); }
        @keyframes pulse-glow {
          0%,100% { box-shadow: 0 0 15px rgba(134,96,248,0.3); }
          50% { box-shadow: 0 0 28px rgba(134,96,248,0.6); }
        }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
      `}</style>
    </div>
  );
}
